import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  useParams,
  useHistory,
  useLocation,
  Link as RouterLink,
} from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../redux/typedHooks";
import { Pause, ArrowLeft, X, ChevronDown } from "lucide-react";
import {
  Avatar,
  Button,
  Typography,
  Tabs,
  Tab,
  Grid,
  useMediaQuery,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Modal,
  Menu,
  MenuItem,
  IconButton,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  deletePostAPI,
  editProfileDataAPI,
  getFileByIdAPI,
  getUserGalleryPostsAPI,
  getUserProfile,
  updateGalleryPostAPI,
  getPaidSubscriptionsByUserAPI,
  checkUserSubscriptionAPI,
} from "../../../api/userApi";
import { Helmet } from "react-helmet";
import useSEO from "../../../seo/use-seo";
import { buildPlatformOrganization } from "../../../seo/platformOrganizationLd";
import { serializeJsonLdForHtmlScript } from "../../../seo/serializeJsonLd";
import { buildCanonicalUrl } from "../../../seo/canonicalUrl";
import { isSearchIndexingBlocked } from "../../../config/searchIndexing";
import { cancelSubscriptionAPI } from "../../../api/ccbillApi";
import {
  sendVideoRequestAPI,
  getLastVideoRequestByUsernameAPI,
} from "../../../api/videoRequestsApi";
import { createConversationAPI } from "../../../api/messageApi";
import {
  getLinkTrackingAPI,
  logLinkClickAPI,
} from "../../../api/linkTrackingApi";
import CCBillPayment from "../../elements/CCBillPayment";
import UnlockPaidContentModal from "../../elements/UnlockPaidContentModal";
import TipDialog from "../../elements/TipDialog";
import ShareLinkDialog, { ShareData } from "../../elements/ShareLinkDialog";
import { CustomVideoRequestDialog } from "../../elements/custom-video-request";
import BaseLoading from "../../Loader/BaseLoading";
import { UserInterface } from "../../../types/user";
import { setUser } from "../../../redux/modules/userSlice";
import { showToast } from "../../../utils/toast";
import { PostDialog } from "../../elements/modal-post";
import {
  trackShareClicked,
  trackShareGenerated,
} from "../../../analytics/canonicalTracking";
import videoIconSvg from "../../../assets/videoIcon.svg";
import defaultAvatars from "../../../assets/defaultAvatars.svg";
import NotificationIcon from "../../../assets/NotificSupIcon";
import VerifiedShieldIcon from "../../../assets/VerifiedSupIcon";
import { useProfileStyles } from "./profile-viewer.styles";
import ProfileBanner from "./profile-banner";
import { ChatProfileIcon } from "../../../assets/svgs/ChatProfileIcon";
import ProfileDollarIcon from "../../../assets/svgs/ProfileDollarIcon";
import locationIcon from "../../../assets/images/location.png";
import torteaIcon from "../../../assets/images/torteaicon.png";
import genderHeartIcon from "../../../assets/images/gender_heart.png";
import messageIcon from "../../../assets/images/message.svg";
import sendVideoRequestIcon from "../../../assets/images/icons/sendVideoRquest.svg";
import dollarIcon from "../../../assets/images/icons/dollarIcon.svg";
import vectorIcons from "../../../assets/images/icons/vectorIcons.svg";
import playButtonIcon from "../../../assets/images/icons/playButton.svg";
import unfoldIcon from "../../../assets/images/icons/unfoldIcon.svg";
import shareProfileIcon from "../../../assets/svgs/shareProfileIcon.svg";
import lockIcon from "../../../assets/svgs/lockIcon.svg";
import markIcon from "../../../assets/svgs/markIcon.svg";
import cancelSubscriptionImage from "../../../assets/images/cancel 1.png";
import subscribeIcon from "../../../assets/images/icons/subscribeIcon.svg";
import profileTabPostIcon from "../../../assets/images/icons/postIcon.svg";
import profileTabPhotosIcon from "../../../assets/images/icons/photosIcon.svg";
import profileTabPlayIcon from "../../../assets/images/icons/playButtonIcon.svg";
import profileTabVideoIcon from "../../../assets/images/icons/videoIcon.svg";
import pencilIcon from "../../../assets/pencilIcon.png";
import cameraOverlayIcon from "../../../assets/mynaui_camera-solid.png";
import GuideSteps, { GuideStep } from "../../elements/GuideSteps";
import EllipsisMenuButtonProfile from "../../elements/EllipsisMenuButtons/EllipsisMenuButtonProfile";
import { useGlobalMonetization } from "../../../hooks/useGlobalMonetization";
import { useCreatorSubscriptionPlansQuery } from "../../../query/hooks/useCreatorSubscriptionPlansQuery";
import { queryKeys } from "../../../query/keys";
import { getCategoriesAPI } from "../../../api/categoriesApi";
import {
  subscribeFreeToCreatorAPI,
} from "../../../api/userApi";

export interface SubscriptionTier {
  id: string;
  tierName: string;
  price: number;
  durationInDays: 30 | 90 | 180;
}

interface ProfileData {
  _id: string;
  firstName: string;
  lastName: string;
  username: string;
  profileImg: string;
  profileBannerImage: string | null;
  validated: boolean;
  postsCount: number;
  subscribeCount: number;
  activeSubscription: {
    tier: string;
  } | null;
  description: string;
  website: string;
  email: string;
  DOB: string;
  gender: string;
  annualIncome: string;
  location: string;
  private: boolean;
  subscriptionPrice: SubscriptionTier[];
  link?: string;
  suggestedTips?: number[];
  minimumTipAmount?: number;
  customContentPrice?: number;
  privacySettings?: {
    allowTipsWithoutSubscription?: boolean;
    hideRealName?: boolean;
  };
  userType?: { _id: string; title: "viewer" | "creator"; description?: string };
}

const PAID_POST_TYPES = new Set([
  "Fixed price video",
  "Fixed price image",
  "Bundle",
]);

const isPaidPostType = (type?: string) => PAID_POST_TYPES.has(type ?? "");

const clampPostPrice = (value: number) => Math.max(0, Math.min(9999, value));

const formatPostPriceInput = (value: number) => `$${clampPostPrice(value).toFixed(2)}`;

const parsePostPriceInput = (rawValue: string) => {
  const centsOnly = rawValue.replace(/\D/g, "");
  if (!centsOnly) return 0;
  return clampPostPrice(Number(centsOnly) / 100);
};

const isCreatorPlanAlreadySubscribed = (plan: any, subscriptions: any[]) => {
  if (!Array.isArray(subscriptions) || subscriptions.length === 0) return false;

  return subscriptions.some((sub: any) => {
    if (plan?.tier && sub?.tier && sub.tier === plan.tier) return true;

    const samePrice =
      typeof sub?.price === "number" &&
      typeof plan?.price === "number" &&
      sub.price === plan.price;
    const sameCurrency =
      !plan?.currency || !sub?.currency || sub.currency === plan.currency;

    return samePrice && sameCurrency;
  });
};

export interface Post {
  _id: string;
  userId: string;
  description: string;
  hashtags?: string[];
  categories?: Array<
    | string
    | {
        _id: string;
        name: string;
        description?: string;
      }
  >;
  type: string;
  price: number;
  content: Array<{
    mediaUrl: string;
    paidContent: boolean;
    type: string;
    originalMediaUrl?: string;
    isBlurred?: boolean;
  }>;
  access: boolean;
  pinnedAt?: string | null; // <— нове
  createdAt: string;
  updatedAt: string;
  commentsCount?: number;
  isBlurred?: boolean;
  subscriptionType?: "basic" | "premium" | null;
}
export const USER_TYPE_IDS = {
  CREATOR: "6800a63485ec077c7c266c25",
  VIEWER: "6800a68085ec077c7c266c27",
} as const;

const isAbsoluteMediaUrl = (value: string) =>
  /^((blob:)|(data:)|(https?:))/i.test(value);

export const ProfilePage: React.FC = () => {
  const seo = useSEO("profile"); // Fallback SEO
  const { id } = useParams<{ id?: string }>();
  const me = useAppSelector<UserInterface | null>(
    state => state.user.currentUser
  );
  const { hasPremiumSubscription } = useGlobalMonetization();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const isViewer = !id || id === me?.id;
  const variant: "viewer" | "creator" = isViewer ? "viewer" : "creator";
  const classes = useProfileStyles({ variant });
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const isWideDesktop = useMediaQuery(theme.breakpoints.up(1440));
  const [profile, setProfile] = useState<ProfileData | null>(null);

  const [posts, setPosts] = useState<{
    totalPages: number;
    items: Post[];
    currentPage: number;
    totalCount: number;
  } | null>(null);
  const [profileAccessMessage, setProfileAccessMessage] = useState<
    string | null
  >(null);
  const [postsType, setPostsType] = useState<
    "all" | "image" | "video" | "custom videos"
  >("all");
  const [requestVideoDialog, setRequestVideoDialog] = useState(false);
  const [lastVideoRequest, setLastVideoRequest] = useState<any | null>(null);
  const [videoRequestInfoOpen, setVideoRequestInfoOpen] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isVideoHovered, setIsVideoHovered] = useState(false);
  const [isVideoExpanded, setIsVideoExpanded] = useState(false);
  const videoInPopupRef = useRef<HTMLVideoElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [mediaUrls, setMediaUrls] = useState<{ [key: string]: string }>({});
  const [loadingMedia, setLoadingMedia] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [subscribeDialogVisible, setSubscribeDialogVisible] = useState(false);
  const [subscribeModalLoading, setSubscribeModalLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState<string>("");
  const [tipDialogVisible, setTipDialogVisible] = useState(false);
  const [subscribeStep, setSubscribeStep] = useState(0);
  const [purchasePostDialogVisible, setPurchasePostDialogVisible] =
    useState(false);
  const [selectedPostForPurchase, setSelectedPostForPurchase] =
    useState<Post | null>(null);
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);
  const [pendingAvatarPreviewUrl, setPendingAvatarPreviewUrl] = useState<
    string | null
  >(null);
  const [pendingBannerFile, setPendingBannerFile] = useState<File | null>(null);
  const [pendingBannerPreviewUrl, setPendingBannerPreviewUrl] = useState<
    string | null
  >(null);
  const [editPostDialogVisible, setEditPostDialogVisible] = useState(false);
  const [selectedPostForEdit, setSelectedPostForEdit] = useState<Post | null>(
    null
  );
  const [editDescription, setEditDescription] = useState("");
  const [editHashtags, setEditHashtags] = useState<string[]>([]);
  const [rawHashtagInput, setRawHashtagInput] = useState<string>("");
  const [editCategories, setEditCategories] = useState<string[]>([]);
  const [editSubscriptionType, setEditSubscriptionType] = useState<
    "basic" | "premium"
  >("basic");
  const [deletePostConfirmOpen, setDeletePostConfirmOpen] = useState(false);
  const [postPendingDelete, setPostPendingDelete] = useState<Post | null>(null);
  const [deletingPost, setDeletingPost] = useState(false);
  const [editPrice, setEditPrice] = useState<number>(0);
  const [editPriceInput, setEditPriceInput] = useState<string>("$0.00");
  const [isSavingPost, setIsSavingPost] = useState(false);
  const [categoryNamesById, setCategoryNamesById] = useState<
    Record<string, string>
  >({});
  const [availableCategories, setAvailableCategories] = useState<
    Array<{ _id: string; name: string; description?: string }>
  >([]);

  // Dynamic SEO state
  const [dynamicSEO, setDynamicSEO] = useState<{
    title: string;
    description: string;
    jsonLd: any;
  } | null>(null);
  const fetchedUrlsRef = useRef<Set<string>>(new Set());

  const subscribeQueryHandledRef = useRef(false);
  const retryCountRef = useRef<Map<string, number>>(new Map());
  const MAX_RETRIES = 2; // Максимальна кількість спроб перезавантаження
  const [handledPostId, setHandledPostId] = useState<string | null>(null);
  const profileBannerRef = useRef<HTMLDivElement>(null);
  const bannerInputRef = useRef<HTMLInputElement | null>(null);
  const avatarInputRef = useRef<HTMLInputElement | null>(null);
  const editPostBodyOverflowRef = useRef<string | null>(null);
  const profileTabsRef = useRef<HTMLDivElement>(null);
  const actionButtonsRef = useRef<HTMLDivElement>(null);
  const tipChatButtonsRef = useRef<HTMLDivElement>(null);

  // Guide steps для profile page (окремі об'єкти для кожного кроку)
  const profileGuideStep1: GuideStep = {
    id: 1,
    title: "Meet our creators",
    description:
      "Like a creator? Follow them for updates and subscribe for exclusive content",
    targetRef: actionButtonsRef,
    position: "bottom",
    offset: { x: 0, y: -100 },
  };

  const profileGuideStep2: GuideStep = {
    id: 2,
    title: "Say Hi",
    description: "Message or tip to connect",
    targetRef: tipChatButtonsRef,
    position: "bottom",
    offset: { x: 0, y: 0 },
  };

  // Generate dynamic SEO and hierarchical JSON-LD schema for profile + creator content
  const generateDynamicSEO = (
    profileData: ProfileData,
    postsData?: { items?: Post[] }
  ) => {
    const fullName = `${profileData.firstName} ${profileData.lastName}`;
    const isCreator = profileData.userType?.title === "creator";
    const profileUrl = `${window.location.origin}/${profileData.username}`;

    const title = `${fullName} (@${profileData.username}) - ${
      isCreator ? "Creator" : "Profile"
    } | Sugar Cribs`;
    const description = profileData.description
      ? `${fullName} on Sugar Cribs. ${profileData.description.substring(
          0,
          150
        )}${profileData.description.length > 150 ? "..." : ""}`
      : `View ${fullName}'s profile on Sugar Cribs. ${
          isCreator
            ? "Exclusive content and subscriptions available."
            : "Connect and explore."
        }`;

    const organization = buildPlatformOrganization(window.location.origin);
    const organizationId = organization["@id"];

    const toAbsoluteUrl = (url?: string) => {
      if (!url) return undefined;
      if (
        url.startsWith("http") ||
        url.startsWith("blob:") ||
        url.startsWith("data:")
      ) {
        return url;
      }
      return url.startsWith("/")
        ? `${window.location.origin}${url}`
        : `${window.location.origin}/${url}`;
    };

    const creatorEntityId = `${profileUrl}#creator`;
    const pageEntityId = `${profileUrl}#profile-page`;
    const contentListEntityId = `${profileUrl}#content-list`;
    const items = Array.isArray(postsData?.items) ? postsData!.items : [];
    const topItems = items.slice(0, 12);

    const postListElements = topItems.map((post, index) => {
      const media = Array.isArray(post.content) ? post.content : [];
      const firstMedia = media[0];
      const mediaUrl = toAbsoluteUrl(firstMedia?.mediaUrl);
      const isVideo = firstMedia?.type === "video";
      const postUrl = `${profileUrl}?postId=${post._id}`;

      return {
        "@type": "ListItem",
        position: index + 1,
        url: postUrl,
        item: {
          "@type": isVideo ? "VideoObject" : "ImageObject",
          "@id": `${profileUrl}#post-${post._id}`,
          name: post.description
            ? post.description.slice(0, 80)
            : `${fullName} content ${index + 1}`,
          description: post.description || undefined,
          contentUrl: mediaUrl,
          thumbnailUrl: mediaUrl,
          uploadDate: post.createdAt || undefined,
          isAccessibleForFree: Boolean(post.access),
        },
      };
    });

    const jsonLd = {
      "@context": "https://schema.org",
      "@graph": [
        organization,
        {
          "@type": "ProfilePage",
          "@id": pageEntityId,
          url: profileUrl,
          name: `${fullName} profile`,
          description,
          publisher: { "@id": organizationId },
          mainEntity: { "@id": creatorEntityId },
          hasPart: postListElements.length
            ? [{ "@id": contentListEntityId }]
            : undefined,
        },
        {
          "@type": "Person",
          "@id": creatorEntityId,
          name: fullName,
          alternateName: profileData.username,
          url: profileUrl,
          description,
          image: toAbsoluteUrl(profileData.profileImg),
          jobTitle: isCreator ? "Content Creator" : undefined,
          knowsAbout: isCreator
            ? ["Content Creation", "Photo Content", "Video Content"]
            : undefined,
          sameAs: profileData.website ? [profileData.website] : undefined,
        },
        ...(postListElements.length
          ? [
              {
                "@type": "ItemList",
                "@id": contentListEntityId,
                name: `${fullName} public content`,
                numberOfItems: postListElements.length,
                itemListElement: postListElements,
              },
            ]
          : []),
      ],
    };

    setDynamicSEO({ title, description, jsonLd });
  };
  const [isAlreadySubscribed, setIsAlreadySubscribed] = useState(false); // Новий стан для перевірки підписки
  const [trialEndsAtMs, setTrialEndsAtMs] = useState<number | null>(null);
  const [isTrialAvailable, setIsTrialAvailable] = useState<boolean>(false);
  const [trialUsed, setTrialUsed] = useState<boolean>(false);
  const [cancelSubscriptionDialogVisible, setCancelSubscriptionDialogVisible] =
    useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState<string | null>(
    null
  );
  const [checkingSubscription, setCheckingSubscription] = useState(false); // Новий ста
  // н для перевірки статусу
  const [lockedByPrivate, setLockedByPrivate] = useState(false); // NEW
  const [subscriptionTier, setSubscriptionTier] = useState<
    "basic" | "premium" | null
  >(null);
  const [trialCheckoutPlanId, setTrialCheckoutPlanId] = useState<string | null>(
    null
  );
  const [currentSubscriptionTier, setCurrentSubscriptionTier] = useState<
    string | null
  >(null);
  const [paidSubscriptions, setPaidSubscriptions] = useState<any[]>([]);
  const [billingMode, setBillingMode] = useState<"monthly" | "weekly">(
    "monthly"
  );
  // const isOwner = !!(me?.id && profile?._id && String(me.id) === String(profile._id));
  // const [pinLoading, setPinLoading] = useState<Record<string, boolean>>({});

  const [pendingCancelModalVisible, setPendingCancelModalVisible] =
    useState(false);
  const [shareMenuEl, setShareMenuEl] = React.useState<null | HTMLElement>(
    null
  );
  const [subscriptionMenuEl, setSubscriptionMenuEl] =
    React.useState<null | HTMLElement>(null);
  const [sharePopupOpen, setSharePopupOpen] = useState(false);
  const [profileShareData, setProfileShareData] = useState<ShareData | null>(
    null
  );
  const restoreBodyScrollAfterEditPost = useCallback(() => {
    if (editPostBodyOverflowRef.current !== null) {
      document.body.style.overflow = editPostBodyOverflowRef.current;
      editPostBodyOverflowRef.current = null;
    }
  }, []);
  useEffect(() => {
    if (editPostDialogVisible || deletePostConfirmOpen) {
      if (editPostBodyOverflowRef.current === null) {
        editPostBodyOverflowRef.current = document.body.style.overflow;
      }
      document.body.style.overflow = "hidden";
      return;
    }
    restoreBodyScrollAfterEditPost();
  }, [editPostDialogVisible, deletePostConfirmOpen, restoreBodyScrollAfterEditPost]);

  useEffect(() => {
    return () => {
      restoreBodyScrollAfterEditPost();
    };
  }, [restoreBodyScrollAfterEditPost]);
  useEffect(() => {
    let isMounted = true;
    const loadCategories = async () => {
      try {
        const response: any = await getCategoriesAPI();
        const categories = Array.isArray(response?.data) ? response.data : [];
        if (isMounted) {
          setAvailableCategories(categories);
        }
        const nextMap = categories.reduce(
          (acc: Record<string, string>, item: { _id?: string; name?: string }) => {
            if (item?._id && item?.name) {
              acc[item._id] = item.name;
            }
            return acc;
          },
          {}
        );
        if (isMounted) {
          setCategoryNamesById(nextMap);
        }
      } catch (error) {
        console.error("Failed to load category names", error);
      }
    };
    loadCategories();
    return () => {
      isMounted = false;
    };
  }, []);
  const profileURL = React.useMemo(() => {
    if (profile?.username) {
      // Use user ID for unique profile link
      return `${window.location.origin}/${profile.username}`;
    }
    return window.location.href;
  }, [profile?.username]);

  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement("textarea");
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      showToast("Link copied");
    } catch (err) {
      console.error("Copy failed", err);
      showToast("Failed to copy", "error");
    }
  };

  const closeSubscriptionMenu = () => setSubscriptionMenuEl(null);

  // Відкриваємо попап відміни підписки, якщо прийшли з рекомендацій з параметром openCancelSubscription=1
  useEffect(() => {
    if (!profile?._id) return;

    const searchParams = new URLSearchParams(location.search);
    const shouldOpen = searchParams.get("openCancelSubscription") === "1";

    if (!shouldOpen) return;

    (async () => {
      try {
        const res: any = await checkUserSubscriptionAPI(profile._id);
        const isActive = Boolean(res?.isActive ?? res?.isSubscribed);
        setIsAlreadySubscribed(isActive);
        setSubscriptionEndDate(
          typeof res?.endDate === "string" ? res.endDate : null
        );
        setIsTrialAvailable(Boolean(res?.isTrialAvailable));
        setTrialUsed(Boolean(res?.trialUsed));
        setCurrentSubscriptionTier(
          typeof res?.tier === "string" ? res.tier.toLowerCase() : null
        );
        setCancelSubscriptionDialogVisible(true);
      } catch (error) {
        console.error("Error fetching subscription status:", error);
        showToast("Failed to load subscription status", "error");
      }
    })();

    // прибираємо параметр з URL, щоб не відкривати попап повторно при навігації назад
    searchParams.delete("openCancelSubscription");
    history.replace({
      ...location,
      search: searchParams.toString() ? `?${searchParams.toString()}` : "",
    });
  }, [profile?._id, location.search, history]);

  const handleLinkClick = async (url: string) => {
    if (!profile?.username) return;

    try {
      const { creatorLink } = await getLinkTrackingAPI(profile.username);
      await logLinkClickAPI(creatorLink, profile.username);
    } catch (err) {
      console.error("Error logging link click:", err);
    }
  };

  const closeShareMenu = () => setShareMenuEl(null);

  function sortWithPins(items: Post[]) {
    // const toTs = (s?: string | null) => (s ? new Date(s).getTime() : 0);
    const toTsStrict = (s?: string) => (s ? new Date(s).getTime() : 0);

    return [...items].sort((a, b) => {
      const aPinned = a.pinnedAt ? 1 : 0;
      const bPinned = b.pinnedAt ? 1 : 0;
      if (aPinned !== bPinned) return bPinned - aPinned; // pinned спершу

      // далі — звичайне сортування як у тебе (newest → oldest)
      return toTsStrict(b.createdAt) - toTsStrict(a.createdAt);
    });
  }

  // const handleTogglePin = async (post: Post) => {
  //   if (!isOwner) return; // тільки власник
  //   const willPin = !post.pinnedAt; // true => pin, false => unpin
  //
  //   setPinLoading((m) => ({ ...m, [post._id]: true }));
  //   // оптимістично оновлюємо UI
  //   setPosts((prev) => {
  //     if (!prev) return prev;
  //     const updated = prev.items.map((p) =>
  //         p._id === post._id ? { ...p, pinnedAt: willPin ? new Date().toISOString() : null } : p
  //     );
  //     return { ...prev, items: sortWithPins(updated) };
  //   });

  //   try {
  //     const res = await togglePinPostAPI(post._id, willPin);
  //     // звіримо з бек-відповіддю (на випадок різниці годинника)
  //     setPosts((prev) => {
  //       if (!prev) return prev;
  //       const updated = prev.items.map((p) =>
  //           p._id === post._id ? { ...p, pinnedAt: res?.pinnedAt ?? (willPin ? new Date().toISOString() : null) } : p
  //       );
  //       return { ...prev, items: sortWithPins(updated) };
  //     });
  //     showToast(res?.pinnedAt ? "Post pinned" : "Post unpinned", "success");
  //   } catch (err) {
  //     // відкотимо оптимістичне оновлення
  //     setPosts((prev) => {
  //       if (!prev) return prev;
  //       const updated = prev.items.map((p) =>
  //           p._id === post._id ? { ...p, pinnedAt: post.pinnedAt ?? null } : p
  //       );
  //       return { ...prev, items: sortWithPins(updated) };
  //     });
  //     showToast(err?.error || "Failed to toggle pin", "error");
  //   } finally {
  //     setPinLoading((m) => ({ ...m, [post._id]: false }));
  //   }
  // };

  const handleShareClick = useCallback(
    async (e?: React.MouseEvent<HTMLElement>) => {
      if (e) {
        e.stopPropagation();
      }

      if (!profile) {
        showToast("Profile data is not available yet.", "error");
        return;
      }

      try {
        await navigator.clipboard.writeText(window.location.href);
        showToast("Profile link copied to clipboard!", "success");
        await logLinkClickAPI(profile._id, "share");

        const meId = me?.id ?? (me as any)?._id;
        const meRole = me?.userType?.title;
        trackShareClicked({
          share_target: "profile",
          user_id: meId,
          user_role: meRole,
          creator_id: profile?._id,
          mvp_feature: "share_profile_intent",
          surface: "profile_viewer",
        });
        trackShareGenerated({
          share_target: "profile",
          share_format: "link",
          share_method: "copy_link",
          user_id: meId,
          user_role: meRole,
          creator_id: profile?._id,
          mvp_feature: "share_profile_link",
          surface: "profile_viewer",
        });
      } catch (error) {
        console.error("Failed to copy profile link:", error);
        showToast("Failed to copy profile link.", "error");
      }
    },
    [profile]
  );

  const handleGoBack = useCallback(() => {
    const previousPath = (history.location.state as any)?.previousPath;
    const scrollPosition = (history.location.state as any)?.scrollPosition;

    if (previousPath) {
      history.push(previousPath, { scrollPosition });
    } else {
      history.goBack();
    }
  }, [history]);

  const shareToTelegram = async () => {
    await handleLinkClick("profile");
    const meId = me?.id ?? (me as any)?._id;
    const meRole = me?.userType?.title;
    trackShareClicked({
      share_target: "profile",
      user_id: meId,
      user_role: meRole,
      creator_id: profile?._id,
      mvp_feature: "share_profile_intent",
      surface: "profile_share_menu",
    });
    trackShareGenerated({
      share_target: "profile",
      share_format: "link",
      share_method: "native_share",
      user_id: meId,
      user_role: meRole,
      creator_id: profile?._id,
      mvp_feature: "share_profile_link",
      surface: "profile_share_menu",
    });
    const text = `Check out ${
      profile?.firstName ?? "this creator"
    }'s profile on SugarCribs!`;
    const url = `https://t.me/share/url?url=${encodeURIComponent(
      profileURL
    )}&text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
    closeShareMenu();
  };

  const shareToWhatsApp = async () => {
    await handleLinkClick("profile");
    const meId = me?.id ?? (me as any)?._id;
    const meRole = me?.userType?.title;
    trackShareClicked({
      share_target: "profile",
      user_id: meId,
      user_role: meRole,
      creator_id: profile?._id,
      mvp_feature: "share_profile_intent",
      surface: "profile_share_menu",
    });
    trackShareGenerated({
      share_target: "profile",
      share_format: "link",
      share_method: "native_share",
      user_id: meId,
      user_role: meRole,
      creator_id: profile?._id,
      mvp_feature: "share_profile_link",
      surface: "profile_share_menu",
    });
    const text = `Check out ${
      profile?.firstName ?? "this creator"
    }'s profile on SugarCribs! ${profileURL}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
    closeShareMenu();
  };

  const shareToFacebook = async () => {
    await handleLinkClick("profile");
    const meId = me?.id ?? (me as any)?._id;
    const meRole = me?.userType?.title;
    trackShareClicked({
      share_target: "profile",
      user_id: meId,
      user_role: meRole,
      creator_id: profile?._id,
      mvp_feature: "share_profile_intent",
      surface: "profile_share_menu",
    });
    trackShareGenerated({
      share_target: "profile",
      share_format: "link",
      share_method: "native_share",
      user_id: meId,
      user_role: meRole,
      creator_id: profile?._id,
      mvp_feature: "share_profile_link",
      surface: "profile_share_menu",
    });
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      profileURL
    )}`;
    window.open(url, "_blank");
    closeShareMenu();
  };

  const shareToX = async () => {
    await handleLinkClick("profile");
    const meId = me?.id ?? (me as any)?._id;
    const meRole = me?.userType?.title;
    trackShareClicked({
      share_target: "profile",
      user_id: meId,
      user_role: meRole,
      creator_id: profile?._id,
      mvp_feature: "share_profile_intent",
      surface: "profile_share_menu",
    });
    trackShareGenerated({
      share_target: "profile",
      share_format: "link",
      share_method: "native_share",
      user_id: meId,
      user_role: meRole,
      creator_id: profile?._id,
      mvp_feature: "share_profile_link",
      surface: "profile_share_menu",
    });
    const text = `Check out ${
      profile?.firstName ?? "this creator"
    }'s profile on SugarCribs!`;
    const url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
      profileURL
    )}&text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
    closeShareMenu();
  };

  const shareByEmail = async () => {
    await handleLinkClick("profile");
    const meId = me?.id ?? (me as any)?._id;
    const meRole = me?.userType?.title;
    trackShareClicked({
      share_target: "profile",
      user_id: meId,
      user_role: meRole,
      creator_id: profile?._id,
      mvp_feature: "share_profile_intent",
      surface: "profile_share_menu",
    });
    trackShareGenerated({
      share_target: "profile",
      share_format: "link",
      share_method: "native_share",
      user_id: meId,
      user_role: meRole,
      creator_id: profile?._id,
      mvp_feature: "share_profile_link",
      surface: "profile_share_menu",
    });
    const subject = `Check out ${
      profile?.firstName ?? "this creator"
    }'s profile on SugarCribs`;
    const body = `Hi! Check out ${
      profile?.firstName ?? "this creator"
    }'s profile on SugarCribs: ${profileURL}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    closeShareMenu();
  };

  useEffect(() => {
    const referrer = document.referrer;
    const hostname = window.location.hostname;
    const ua = navigator.userAgent.toLowerCase();

    // Перевіряємо зовнішній реферер
    const isExternalReferrer = referrer && !referrer.includes(hostname);

    // Перевіряємо, чи це перехід з апки (Telegram, Instagram, Facebook тощо)
    const isExternalApp = /telegram|instagram|fbav|facebook/i.test(ua);

    // Логувати тільки якщо зовнішній сайт або апка
    if (!isExternalReferrer && !isExternalApp) return;
    const logLinkClick = async () => {};

    logLinkClick();
  }, []);

  const creatorId = profile?._id; // або як в тебе називається цей юзер
  const openSubscribeDialogWithLoader = useCallback(async () => {
    setSubscribeDialogVisible(true);
    setSubscribeStep(0);
    setSelectedTier("");
    setSubscribeModalLoading(true);

    try {
      if (!creatorId) return;
      const res: any = await checkUserSubscriptionAPI(creatorId);
      const isActive = Boolean(res?.isActive ?? res?.isSubscribed);
      setIsAlreadySubscribed(isActive);
      setSubscriptionEndDate(
        typeof res?.endDate === "string" ? res.endDate : null
      );
      setCurrentSubscriptionTier(
        typeof res?.tier === "string" ? res.tier.toLowerCase() : null
      );
      setIsTrialAvailable(Boolean(res?.isTrialAvailable));
      setTrialUsed(Boolean(res?.trialUsed));
    } catch (e) {
      console.error("❌ Failed to fetch subscription status", e);
    } finally {
      setSubscribeModalLoading(false);
    }
  }, [creatorId]);

  const refetchSubscriptionStatus = useCallback(async () => {
    if (!creatorId) return;
    try {
      const res: any = await checkUserSubscriptionAPI(creatorId);
      // #region agent log
      fetch("http://127.0.0.1:7574/ingest/4c4a54fc-6a32-45fd-bdf3-4c3c66ca0dc3", { method: "POST", headers: { "Content-Type": "application/json", "X-Debug-Session-Id": "01f43b" }, body: JSON.stringify({ sessionId: "01f43b", runId: "pre-fix-trial", hypothesisId: "T1", location: "profile-viewer.tsx:851", message: "subscription status refetched", data: { creatorId, isTrialAvailable: res?.isTrialAvailable, isActive: res?.isActive, isSubscribed: res?.isSubscribed, tier: res?.tier, cancelledAt: res?.cancelledAt || null }, timestamp: Date.now() }) }).catch(() => {});
      // #endregion
      const isActive = Boolean(res?.isActive ?? res?.isSubscribed);
      setIsAlreadySubscribed(isActive);
      setSubscriptionEndDate(
        typeof res?.endDate === "string" ? res.endDate : null
      );
      setCurrentSubscriptionTier(
        typeof res?.tier === "string" ? res.tier.toLowerCase() : null
      );
      setIsTrialAvailable(Boolean(res?.isTrialAvailable));
      setTrialUsed(Boolean(res?.trialUsed));
    } catch {
      // ignore
    }
  }, [creatorId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldOpenSubscribe = params.get("subscribe") === "1";
    if (!shouldOpenSubscribe) {
      subscribeQueryHandledRef.current = false;
      return;
    }
    if (subscribeQueryHandledRef.current) return;
    subscribeQueryHandledRef.current = true;

    openSubscribeDialogWithLoader();
    params.delete("subscribe");
    history.replace({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : "",
    });
  }, [
    history,
    location.pathname,
    location.search,
    openSubscribeDialogWithLoader,
  ]);

  const TRIAL_DAYS = 7;
  const trialStorageKey = "creatorTrials:v1";
  const trialAfterText = useMemo(() => {
    const afterDate = new Date(Date.now() + TRIAL_DAYS * 24 * 60 * 60 * 1000);
    const formattedDate = afterDate.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
    });
    return `$19.99/month after ${formattedDate}`;
  }, [TRIAL_DAYS]);
  const trialRecurringPrice = useMemo(() => {
    const match = trialAfterText.match(/\$(\d+(?:\.\d+)?)/);
    return match ? Number(match[1]) : 19.99;
  }, [trialAfterText]);
  const isTrialActive = useMemo(() => {
    if (!creatorId || !trialEndsAtMs) return false;
    return Date.now() < trialEndsAtMs;
  }, [creatorId, trialEndsAtMs]);
  const isSubscribedOrTrial = isAlreadySubscribed || isTrialActive;
  const effectiveSubscriptionTier = useMemo<"basic" | "premium" | null>(() => {
    if (subscriptionTier) return subscriptionTier;
    // Припущення: trial відкриває premium контент (можемо змінити на "basic", якщо треба).
    return isTrialActive ? "premium" : null;
  }, [subscriptionTier, isTrialActive]);

  useEffect(() => {
    if (!creatorId) return;
    try {
      const raw = localStorage.getItem(trialStorageKey);
      const parsed = raw
        ? (JSON.parse(raw) as Record<string, { endsAtMs: number }>)
        : {};
      const endsAtMs = parsed?.[creatorId]?.endsAtMs;
      if (
        typeof endsAtMs === "number" &&
        Number.isFinite(endsAtMs) &&
        endsAtMs > Date.now()
      ) {
        setTrialEndsAtMs(endsAtMs);
      } else {
        setTrialEndsAtMs(null);
      }
    } catch {
      setTrialEndsAtMs(null);
    }
  }, [creatorId]);

  const persistTrial = useCallback(
    (endsAtMs: number) => {
      if (!creatorId) return;
      try {
        const raw = localStorage.getItem(trialStorageKey);
        const parsed = raw
          ? (JSON.parse(raw) as Record<string, { endsAtMs: number }>)
          : {};
        parsed[creatorId] = { endsAtMs };
        localStorage.setItem(trialStorageKey, JSON.stringify(parsed));
      } catch {
        // ignore
      }
    },
    [creatorId]
  );

  const handleSubscribeFree = useCallback(async () => {
    if (!creatorId) return;
    try {
      await subscribeFreeToCreatorAPI(creatorId);
      showToast("Free subscription activated", "success");
    } catch (e: any) {
      showToast(e?.message || "Free subscription is not available", "error");
    } finally {
      await refetchSubscriptionStatus();
    }
  }, [creatorId, refetchSubscriptionStatus]);

  const loadId = useMemo(() => id || me?.id, [id, me?.id]);
  const loadUsername = useMemo(() => id || me?.username, [id, me?.username]);
  const isOwner = useMemo(() => {
    const meId = me?.id ?? me?._id;
    return !!(meId && profile?._id && String(meId) === String(profile._id));
  }, [me?.id, me?._id, profile?._id]);
  const isAdmin = useMemo(
    () => !!(me?.isAdmin || me?.isSuperAdmin),
    [me?.isAdmin, me?.isSuperAdmin]
  );
  const canViewAllPosts = useMemo(
    () => isOwner || isAdmin || isSubscribedOrTrial,
    [isOwner, isAdmin, isSubscribedOrTrial]
  );
  const fetchMediaUrl = useCallback(
    async (mediaUrl: string, type: string = "image") => {
      if (!mediaUrl || mediaUrl.trim() === "") {
        return defaultAvatars;
      }
      // Якщо це Google URL (lh3.googleusercontent.com), використовуємо його напряму
      if (mediaUrl.includes("lh3.googleusercontent.com")) {
        setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
        setMediaUrls(prev => ({ ...prev, [mediaUrl]: mediaUrl }));
        return mediaUrl;
      }
      // Якщо це пряме посилання (https:// або http://), використовуємо його напряму
      if (mediaUrl.startsWith("https://") || mediaUrl.startsWith("http://")) {
        setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
        setMediaUrls(prev => ({ ...prev, [mediaUrl]: mediaUrl }));
        return mediaUrl;
      }
      const fileId = mediaUrl.split("/api/files/")[1];
      if (!fileId || fileId.trim() === "") {
        setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
        return defaultAvatars;
      }
      setLoadingMedia(prev => ({ ...prev, [mediaUrl]: true }));
      try {
        const blobUrl = await getFileByIdAPI(fileId);
        // Перевіряємо, чи blob URL валідний
        if (!blobUrl || typeof blobUrl !== "string") {
          throw new Error("Invalid blob URL received");
        }
        // Скидаємо лічильник спроб при успішному завантаженні
        retryCountRef.current.delete(mediaUrl);
        setMediaUrls(prev => ({ ...prev, [mediaUrl]: blobUrl }));
        return blobUrl;
      } catch (err) {
        console.error(`Error fetching ${type} for ${mediaUrl}:`, err);
        // Не показуємо toast для avatar, якщо його просто немає
        if (type !== "avatar") {
          showToast(`Failed to load ${type}`, "error");
        }
        // Видаляємо з fetchedUrls, щоб можна було спробувати знову
        fetchedUrlsRef.current.delete(mediaUrl);
        return defaultAvatars;
      } finally {
        setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
      }
    },
    []
  );
  const resolveMediaUrl = useCallback(
    (key?: string | null) => {
      if (!key) return undefined;
      if (mediaUrls[key]) return mediaUrls[key];
      if (isAbsoluteMediaUrl(key)) return key;
      return undefined;
    },
    [mediaUrls]
  );
  useEffect(() => {
    // Позначаємо всі знайдені медіафайли як "в завантаженні", щоб не мерехтіли плейсхолдери до старту fetch
    const pendingLoad: Record<string, boolean> = {};
    const markPending = (key?: string | null) => {
      if (!key || key.trim() === "") return;
      pendingLoad[key] = true;
    };

    if (profile?.profileImg && profile.profileImg.trim() !== "") {
      markPending(profile.profileImg);
    }
    if (
      profile?.profileBannerImage &&
      profile.profileBannerImage.trim() !== ""
    ) {
      markPending(profile.profileBannerImage);
    }
    if (posts?.items) {
      posts.items.forEach(post => {
        post.content.forEach(contentItem => {
          markPending(contentItem.mediaUrl);
        });
      });
    }

    if (Object.keys(pendingLoad).length) {
      setLoadingMedia(prev => {
        let changed = false;
        const next = { ...prev };
        Object.keys(pendingLoad).forEach(key => {
          if (prev[key] === undefined) {
            next[key] = true;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }

    // Якщо profileImg порожній або невалідний, встановлюємо loading в false одразу
    if (!profile?.profileImg || profile.profileImg.trim() === "") {
      // Очищаємо loadingMedia для будь-яких попередніх значень profileImg
      setLoadingMedia(prev => {
        const next = { ...prev };
        // Знаходимо всі ключі, які можуть бути profileImg
        Object.keys(next).forEach(key => {
          if (key.includes("profileImg") || key === profile?.profileImg) {
            next[key] = false;
          }
        });
        return next;
      });
    } else if (
      profile.profileImg &&
      profile.profileImg.trim() !== "" &&
      !fetchedUrlsRef.current.has(profile.profileImg)
    ) {
      fetchedUrlsRef.current.add(profile.profileImg);
      fetchMediaUrl(profile.profileImg, "avatar");
    }

    if (
      profile?.profileBannerImage &&
      profile.profileBannerImage.trim() !== "" &&
      !fetchedUrlsRef.current.has(profile.profileBannerImage)
    ) {
      fetchedUrlsRef.current.add(profile.profileBannerImage);
      fetchMediaUrl(profile.profileBannerImage, "banner");
    }

    if (posts?.items) {
      posts.items.forEach(post => {
        post.content.forEach(contentItem => {
          if (
            contentItem.mediaUrl &&
            !fetchedUrlsRef.current.has(contentItem.mediaUrl)
          ) {
            fetchedUrlsRef.current.add(contentItem.mediaUrl);
            fetchMediaUrl(contentItem.mediaUrl, contentItem.type);
          }
        });
      });
    }
  }, [profile, posts, fetchMediaUrl]);

  // useEffect(() => {
  //   const currentFetchedUrls = fetchedUrlsRef.current;
  //   return () => {
  //     // Відкликаємо blob URL тільки при unmount компонента, не при зміні posts
  //     // Це запобігає передчасному відкликанню blob URL, які все ще використовуються
  //     Object.values(mediaUrls).forEach(url => {
  //       if (typeof url === "string" && url.startsWith("blob:")) {
  //         URL.revokeObjectURL(url);
  //       }
  //     });
  //     setMediaUrls({});
  //     setLoadingMedia({});
  //     currentFetchedUrls.clear();
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  useEffect(() => {
    if (variant === "creator" && profile?._id && me?.id) {
      setCheckingSubscription(true);
      Promise.allSettled([
        getPaidSubscriptionsByUserAPI(me.id),
        checkUserSubscriptionAPI(profile._id),
      ])
        .then(results => {
          const paidSubsResult = results[0];
          const statusResult = results[1];

          const paidItems =
            paidSubsResult.status === "fulfilled" &&
            Array.isArray((paidSubsResult.value as any)?.items)
              ? (paidSubsResult.value as any).items
              : [];

          setPaidSubscriptions(paidItems);

          const activeForCreator = paidItems.find(
            (item: any) => item?.creatorId?._id === profile._id
          );

          const tier = activeForCreator?.tier;
          if (tier === "basic" || tier === "premium") {
            setSubscriptionTier(tier);
          } else {
            setSubscriptionTier(null);
          }

          // Source of truth for UI: checkSubscription (враховує free|trial|basic|premium)
          const status =
            statusResult.status === "fulfilled" ? statusResult.value : null;
          const isSubscribedFromStatus = Boolean(
            (status as any)?.isActive ?? (status as any)?.isSubscribed
          );

          setIsAlreadySubscribed(isSubscribedFromStatus || !!activeForCreator);

          const endDate = (status as any)?.endDate;
          setSubscriptionEndDate(typeof endDate === "string" ? endDate : null);
          setIsTrialAvailable(Boolean((status as any)?.isTrialAvailable));
          setTrialUsed(Boolean((status as any)?.trialUsed));
          const activeTier =
            typeof activeForCreator?.tier === "string"
              ? String(activeForCreator.tier).toLowerCase()
              : null;

          const tierFromStatus = (status as any)?.tier;
          setCurrentSubscriptionTier(
            typeof tierFromStatus === "string"
              ? tierFromStatus.toLowerCase()
              : activeTier
          );
          if (tierFromStatus === "basic" || tierFromStatus === "premium") {
            setSubscriptionTier(tierFromStatus);
          } else if (!activeForCreator) {
            setSubscriptionTier(null);
          }
        })
        .catch(err => {
          console.error("🔴 Subscription check failed:", err);
          showToast("Не вдалося перевірити статус підписки", "error");
          setIsAlreadySubscribed(false);
          setSubscriptionEndDate(null);
          setSubscriptionTier(null);
          setCurrentSubscriptionTier(null);
          setIsTrialAvailable(false);
        })
        .finally(() => {
          setCheckingSubscription(false);
        });
    }
  }, [profile?._id, me?.id, variant]);

  const bannerImageSrc =
    pendingBannerPreviewUrl || resolveMediaUrl(profile?.profileBannerImage);
  const displayAvatarSrc =
    pendingAvatarPreviewUrl ||
    ((profile?.profileImg && mediaUrls[profile.profileImg]) || defaultAvatars);
  const avatarImageSrc = profile?.profileImg
    ? resolveMediaUrl(profile.profileImg)
    : defaultAvatars;
  const shouldHideRealNameOnProfile = Boolean(
    !isOwner && profile?.privacySettings?.hideRealName === true
  );
  const profileDisplayName = shouldHideRealNameOnProfile
    ? `@${profile?.username || ""}`
    : `${profile?.firstName || ""} ${profile?.lastName || ""}`.trim() ||
      `@${profile?.username || ""}`;
  const handleOpenSharePopup = useCallback(() => {
    if (!profile) return;
    setProfileShareData({
      url: profileURL,
      title: `${profileDisplayName} (@${profile.username})`,
      text: profile.description || "",
      imgUrl:
        profile.profileImg && resolveMediaUrl(profile.profileImg)
          ? resolveMediaUrl(profile.profileImg)!
          : defaultAvatars,
    });
    setSharePopupOpen(true);
  }, [profile, profileDisplayName, profileURL, resolveMediaUrl]);
  const isBannerLoading = Boolean(
    profile?.profileBannerImage && loadingMedia[profile.profileBannerImage]
  );
  const hasPendingProfileMediaChanges = Boolean(
    pendingAvatarFile || pendingBannerFile
  );
  const bannerLoadingState = isBannerLoading;

  const handleBannerSelected = useCallback(
    (file: File) => {
      if (!file) return;
      if (!isOwner) {
        showToast("You can only update your own banner", "error");
        return;
      }
      if (!file.type.startsWith("image/")) {
        showToast("Please choose an image file", "error");
        return;
      }
      const maxSizeMb = 10;
      if (file.size > maxSizeMb * 1024 * 1024) {
        showToast(`Image must be smaller than ${maxSizeMb}MB`, "error");
        return;
      }
      setPendingBannerFile(file);
      setPendingBannerPreviewUrl(prevPreviewUrl => {
        if (prevPreviewUrl) {
          URL.revokeObjectURL(prevPreviewUrl);
        }
        return URL.createObjectURL(file);
      });
    },
    [isOwner]
  );
  const handleBannerFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleBannerSelected(file);
      }
      event.target.value = "";
    },
    [handleBannerSelected]
  );
  const handleOpenAvatarFileDialog = useCallback(
    (event?: React.MouseEvent) => {
      event?.preventDefault();
      event?.stopPropagation();
      if (!isOwner) return;
      avatarInputRef.current?.click();
    },
    [isOwner]
  );
  const handleAvatarFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        event.target.value = "";
        return;
      }
      if (!file.type.startsWith("image/")) {
        showToast("Please choose an image file", "error");
        event.target.value = "";
        return;
      }
      const maxSizeMb = 10;
      if (file.size > maxSizeMb * 1024 * 1024) {
        showToast(`Image must be smaller than ${maxSizeMb}MB`, "error");
        event.target.value = "";
        return;
      }
      setPendingAvatarFile(file);
      setPendingAvatarPreviewUrl(prevPreviewUrl => {
        if (prevPreviewUrl) {
          URL.revokeObjectURL(prevPreviewUrl);
        }
        return URL.createObjectURL(file);
      });
      event.target.value = "";
    },
    []
  );
  const handleCancelPendingMediaChanges = useCallback(() => {
    setPendingAvatarFile(null);
    setPendingAvatarPreviewUrl(prevPreviewUrl => {
      if (prevPreviewUrl) {
        URL.revokeObjectURL(prevPreviewUrl);
      }
      return null;
    });
    setPendingBannerFile(null);
    setPendingBannerPreviewUrl(prevPreviewUrl => {
      if (prevPreviewUrl) {
        URL.revokeObjectURL(prevPreviewUrl);
      }
      return null;
    });
  }, []);
  useEffect(() => {
    return () => {
      if (pendingAvatarPreviewUrl) {
        URL.revokeObjectURL(pendingAvatarPreviewUrl);
      }
    };
  }, [pendingAvatarPreviewUrl]);
  useEffect(() => {
    return () => {
      if (pendingBannerPreviewUrl) {
        URL.revokeObjectURL(pendingBannerPreviewUrl);
      }
    };
  }, [pendingBannerPreviewUrl]);
  const saveAvatarMutation = useMutation({
    mutationFn: async () => {
      if (!profile) {
        throw new Error("Profile is not loaded");
      }
      if (!pendingAvatarFile && !pendingBannerFile) {
        throw new Error("No pending media changes");
      }
      const formData = new FormData();
      formData.append("firstName", profile.firstName ?? "");
      formData.append("lastName", profile.lastName ?? "");
      formData.append("username", profile.username ?? "");
      formData.append("bio", profile.description ?? "");
      formData.append("website", profile.website ?? "");
      formData.append("email", profile.email ?? "");
      formData.append("gender", profile.gender ?? "");
      formData.append("location", profile.location ?? "");
      formData.append(
        "annualIncome",
        profile.annualIncome ? String(profile.annualIncome) : ""
      );
      if (profile.DOB) {
        formData.append("DOB", profile.DOB);
      }
      if (typeof profile.minimumTipAmount === "number") {
        formData.append("minimumTipAmount", String(profile.minimumTipAmount));
      }
      if (typeof profile.customContentPrice === "number") {
        formData.append("customContentPrice", String(profile.customContentPrice));
      }
      if (profile.link) {
        formData.append("link", profile.link);
      }
      if (pendingBannerFile) {
        formData.append("profileBannerImage", pendingBannerFile);
      } else if (profile.profileBannerImage) {
        formData.append("profileBannerImage", profile.profileBannerImage);
      }
      if (pendingAvatarFile) {
        formData.append("image", pendingAvatarFile);
      }
      return editProfileDataAPI(formData);
    },
    onSuccess: async response => {
      const updatedUser =
        response?.data?.user ?? response?.user ?? response?.data ?? null;
      const updatedAvatar =
        response?.data?.profileImg ??
        response?.profileImg ??
        response?.data?.user?.profileImg ??
        null;
      const updatedBanner =
        response?.data?.profileBannerImage ??
        response?.profileBannerImage ??
        response?.data?.user?.profileBannerImage ??
        null;

      if (pendingAvatarFile) {
        const avatarKey = updatedAvatar || profile?.profileImg;
        if (avatarKey) {
          const previewUrl = URL.createObjectURL(pendingAvatarFile);
          setProfile(prev =>
            prev
              ? {
                  ...prev,
                  profileImg: updatedAvatar || prev.profileImg,
                }
              : prev
          );
          setMediaUrls(prev => ({
            ...prev,
            [avatarKey]: previewUrl,
          }));
          fetchedUrlsRef.current.delete(avatarKey);
          fetchedUrlsRef.current.add(avatarKey);
        }
      }

      if (pendingBannerFile) {
        const bannerKey = updatedBanner || profile?.profileBannerImage;
        const bannerPreviewUrl = URL.createObjectURL(pendingBannerFile);
        setProfile(prev =>
          prev
            ? {
                ...prev,
                profileBannerImage: updatedBanner || prev.profileBannerImage,
              }
            : prev
        );
        setMediaUrls(prev => ({
          ...prev,
          ...(bannerKey ? { [bannerKey]: bannerPreviewUrl } : {}),
        }));
        if (bannerKey) {
          fetchedUrlsRef.current.delete(bannerKey);
          fetchedUrlsRef.current.add(bannerKey);
        }
      }
      if (updatedUser && me) {
        dispatch(
          setUser({
            ...me,
            ...updatedUser,
          })
        );
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.user });
      handleCancelPendingMediaChanges();
      showToast("Profile images updated successfully", "success");
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Failed to update profile media";
      showToast(message, "error");
    },
  });
  const handleSavePendingAvatar = useCallback(() => {
    if (!hasPendingProfileMediaChanges || saveAvatarMutation.isPending) return;
    saveAvatarMutation.mutate();
  }, [hasPendingProfileMediaChanges, saveAvatarMutation]);

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
    const tabTypes = ["all", "image", "video", "custom videos"];
    handleChangePostType(tabTypes[newValue]);
  };

  function normalizeUserType(d: any): ProfileData["userType"] | undefined {
    const ut = d?.userType ?? d?.usertype ?? d?.user_type;
    if (!ut) return undefined;
    if (typeof ut === "string") {
      if (ut === USER_TYPE_IDS.CREATOR) return { _id: ut, title: "creator" };
      if (ut === USER_TYPE_IDS.VIEWER) return { _id: ut, title: "viewer" };
      return undefined;
    }
    const id = ut._id || ut.id || "";
    const raw = (ut.title || ut.name || "").toLowerCase();
    if (raw === "viewer" || raw === "creator") {
      return { _id: id, title: raw, description: ut.description };
    }
    return undefined;
  }

  const isProfileViewer = (() => {
    const title = profile?.userType?.title;
    if (title === "viewer") return true;
    if (title === "creator") return false;
    const tiers = Array.isArray(profile?.subscriptionPrice)
      ? profile!.subscriptionPrice
      : [];
    const hasPaidTier = tiers.some(t => (t?.price ?? 0) > 0);
    const hasCustomPrice = (profile?.customContentPrice ?? 0) > 0;
    return !(hasPaidTier || hasCustomPrice);
  })();

  useEffect(() => {
    if (!loadId || !loadUsername) {
      setLoading(false);
      return;
    }
    setProfile(null);
    setPosts(null);
    setLoading(true);
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const data = await getUserProfile(
          loadId !== "settings" ? loadId : me?.username
        );
        const subscriptionPrice =
          Array.isArray(data.subscriptionPrice) && data.subscriptionPrice.length
            ? data.subscriptionPrice
            : [{ tierName: "Premium", price: 50, durationInDays: 30 }];
        const suggestedTips =
          Array.isArray(data.suggestedTips) && data.suggestedTips.length
            ? data.suggestedTips
            : [5, 10, 15, 20, 25];
        const privacySettings =
          (data as any)?.privacySettings ||
          (data as any)?.details?.privacySettings ||
          {};
        const profileData = {
          _id: data._id,
          firstName: data.firstName,
          lastName: data.lastName,
          username: data.username,
          profileImg: data.profileImg,
          validated: data.validated,
          postsCount: data.postsCount,
          subscribeCount:
            (data as any).subscribeCount ?? (data as any).subscribersCount ?? 0,
          activeSubscription: data.activeSubscription,
          profileBannerImage: data.profileBannerImage,
          description: data.bio,
          website: data.website,
          link: data.link,
          email: data.email,
          DOB: data.DOB,
          gender: data.gender,
          annualIncome: data.annualIncome,
          location: data.location,
          private: data.private,
          subscriptionPrice,
          suggestedTips,
          minimumTipAmount: data.minimumTipAmount || 2,
          customContentPrice: data.customContentPrice || 100,
          privacySettings: {
            allowTipsWithoutSubscription:
              privacySettings?.allowTipsWithoutSubscription === true,
            hideRealName: privacySettings?.hideRealName === true,
          },
          userType: normalizeUserType(data),
        };
        setProfile(profileData);

        // Generate dynamic SEO
        generateDynamicSEO(profileData);

        // Fetch posts with profile data
        await fetchPosts(profileData);
      } catch (err) {
        const error = err as Error;
        console.error("Error loading profile:", error);
      } finally {
        setLoading(false);
      }
    };
    const fetchPosts = async (profileData: ProfileData | null = null) => {
      setLoading(true);
      const options = { page: 1, perPage: 15 };

      try {
        const data = await getUserGalleryPostsAPI(loadId, options);

        const isLocked =
          (data as any)?.status === "private_requires_subscription" ||
          data?.message === "This profile is private" ||
          data?.message === "This profile is restricted to followers only" ||
          data?.message === "Content is hidden. Follow to view.";
        // eslint-disable-next-line react-hooks/exhaustive-deps
        setLockedByPrivate(!!isLocked);
        setProfileAccessMessage(
          isLocked
            ? data?.message ||
                "This is a private profile. Subscribe to unlock the gallery."
            : null
        );

        // ГОЛОВНЕ: не null — показуємо стрічку (бек уже повертає заблюрені items)
        const rawItems = Array.isArray(data?.items) ? data.items : [];

        // Якщо це мій власний профіль або я підписаний — знімаємо блюр локально
        const items =
          loadId === me?.id || isAlreadySubscribed
            ? rawItems.map((post: Post) => ({
                ...post,
                content: post.content.map(c => ({ ...c, isBlurred: false })),
              }))
            : rawItems;

        const postsPayload = { ...data, items };
        setPosts(postsPayload);
        if (profileData) {
          generateDynamicSEO(profileData, postsPayload);
        }
      } catch (err) {
        const error = err as Error;
        showToast(error.message || "Error loading posts!", "error");
        console.error("Error loading posts:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [loadId, loadUsername, me?.id, me?.username, isAlreadySubscribed]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const postId = searchParams.get("postId");

    if (postId && posts?.items && !dialogOpen && handledPostId !== postId) {
      const post = posts.items.find(p => p._id === postId);
      if (post) {
        // Блокуємо відкриття модального вікна, якщо користувач не має доступу
        if (!canViewAllPosts) {
          showToast("Subscribe to unlock content", "info");
          // Видаляємо postId з URL, щоб не відкривалося модальне вікно
          history.replace({
            pathname: history.location.pathname,
            search: "",
          });
          return;
        }
        const paidLockedNoAccess =
          post.price > 0 &&
          !post.access &&
          !(me?.isAdmin || me?.isSuperAdmin) &&
          !isOwner;
        if (paidLockedNoAccess) {
          if (me?.id) {
            handlePurchasePost(post);
          } else {
            showToast("You must be logged in to purchase content.", "error");
          }
          setHandledPostId(postId);
          history.replace({
            pathname: history.location.pathname,
            search: "",
          });
          return;
        }
        setSelectedPost(post);
        setDialogOpen(true);
        setHandledPostId(postId);
      }
    }
  }, [
    location.search,
    posts,
    dialogOpen,
    handledPostId,
    canViewAllPosts,
    history,
    isOwner,
    me?.id,
    me?.isAdmin,
    me?.isSuperAdmin,
  ]);

  const { data: creatorPlansRaw } = useCreatorSubscriptionPlansQuery(
    profile?._id
  );
  const creatorPlans = useMemo(() => {
    if (Array.isArray(creatorPlansRaw)) return creatorPlansRaw;
    return (creatorPlansRaw as any)?.items ?? [];
  }, [creatorPlansRaw]);
  const creatorPlansMeta = useMemo(() => {
    if (Array.isArray(creatorPlansRaw)) return {};
    return (creatorPlansRaw as any)?.meta ?? {};
  }, [creatorPlansRaw]);

  const freeEnabled = Boolean((creatorPlansMeta as any)?.freeEnabled);
  const trialEnabled = Boolean((creatorPlansMeta as any)?.trialEnabled);

  const displayCreatorPlans = React.useMemo(() => {
    const plans = Array.isArray(creatorPlans) ? [...creatorPlans] : [];

    return plans.filter(plan => {
      const tier = String(plan?.tier || "").toLowerCase();
      const isFreePlan = Boolean((plan as any)?.isFree) || tier === "free";

      // Підписаним теж показуємо плани (Upgrade subscription / зміна рівня).
      // Статус по кожній картці — через isPlanSubscribed / «Subscribed».

      // Free: безкоштовний рівень не показуємо, якщо вже є активна підписка на цього креатора
      if (isFreePlan) return freeEnabled && !isAlreadySubscribed;

      // Trial
      if (tier === "trial") return trialEnabled && isTrialAvailable;

      // Paid tiers (basic/premium/others)
      return tier !== "freemium";
    });
  }, [
    creatorPlans,
    freeEnabled,
    isAlreadySubscribed,
    isTrialAvailable,
    trialEnabled,
  ]);
  const singleFreePlan = useMemo(() => {
    if (!Array.isArray(creatorPlans)) return null;
    const freePlans = creatorPlans.filter(plan => {
      const tier = String((plan as any)?.tier || "").toLowerCase();
      return Boolean((plan as any)?.isFree) || tier === "free";
    });
    if (freePlans.length !== 1) return null;
    const onlyPlan = freePlans[0] as any;
    const isDefaultPlan = Boolean(
      onlyPlan?.isDefault ||
      onlyPlan?.default ||
      onlyPlan?.isDefaultPlan ||
      onlyPlan?.isDefaultSubscription
    );
    if (isDefaultPlan || creatorPlans.length === 1) {
      return onlyPlan;
    }
    return null;
  }, [creatorPlans]);
  const shouldInstantFreeSubscribe = Boolean(
    !isSubscribedOrTrial && freeEnabled && singleFreePlan
  );
  useEffect(() => {
    if (subscribeDialogVisible && displayCreatorPlans.length && !selectedTier) {
      setSelectedTier(displayCreatorPlans[0]?.name || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribeDialogVisible, displayCreatorPlans]);

  const creatorSubscriptions = React.useMemo(() => {
    if (!Array.isArray(paidSubscriptions) || !profile?._id) return [];
    return paidSubscriptions.filter((sub: any) => {
      const subCreatorId =
        typeof sub.creatorId === "string" ? sub.creatorId : sub.creatorId?._id;
      return subCreatorId === profile._id;
    });
  }, [paidSubscriptions, profile?._id]);

  const hasAnyActiveSubscriptionToCreator =
    Array.isArray(creatorSubscriptions) && creatorSubscriptions.length > 0;

  const isPlanSubscribed = useCallback(
    (plan: any) => isCreatorPlanAlreadySubscribed(plan, creatorSubscriptions),
    [creatorSubscriptions]
  );

  const hasUpgradeSubscriptionOptions = useMemo(() => {
    if (!isAlreadySubscribed) return false;

    return displayCreatorPlans.some((plan: any) => {
      const tierLc = String(plan?.tier || "").toLowerCase();
      const isFreePlan = Boolean((plan as any)?.isFree) || tierLc === "free";
      const isTrialPlan = tierLc === "trial";

      if (isFreePlan || isTrialPlan) return false;

      return !isPlanSubscribed(plan);
    });
  }, [displayCreatorPlans, isAlreadySubscribed, isPlanSubscribed]);

  const hasPaidSubscriptionSetup = useMemo(() => {
    const plans = Array.isArray(creatorPlans) ? creatorPlans : [];
    const hasPaidCreatorPlan = plans.some((plan: any) => {
      const tier = String(plan?.tier || "").toLowerCase();
      const isFreeOrTrial =
        Boolean(plan?.isFree) || tier === "free" || tier === "trial";
      const planPrice = Number(plan?.price ?? plan?.amount ?? 0);
      return !isFreeOrTrial && Number.isFinite(planPrice) && planPrice > 0;
    });

    const hasPaidLegacyTier = Array.isArray(profile?.subscriptionPrice)
      ? profile.subscriptionPrice.some(t => Number(t?.price ?? 0) > 0)
      : false;

    return hasPaidCreatorPlan || hasPaidLegacyTier;
  }, [creatorPlans, profile?.subscriptionPrice]);

  const shouldShowPaidSubscriptionLock =
    variant === "creator" &&
    !isViewer &&
    !isOwner &&
    !isAdmin &&
    !isSubscribedOrTrial &&
    hasPaidSubscriptionSetup;

  if (loading) return <BaseLoading />;
  if (!profile) return <Typography>Profile not found</Typography>;

  const handleChangePostType = (type: string) => {
    const normalizedType = type.toLowerCase();
    let newPostsType: "all" | "image" | "video" | "custom videos" = "all";
    if (normalizedType === "all") {
      newPostsType = "all";
    } else if (normalizedType === "image") {
      newPostsType = "image";
    } else if (normalizedType === "video") {
      newPostsType = "video";
    } else if (normalizedType === "custom videos") {
      newPostsType = "custom videos";
    }
    setPostsType(newPostsType);
    setPosts({ totalPages: 0, items: [], currentPage: 1, totalCount: 0 });
    if (newPostsType !== "custom videos") {
      const postTypes = newPostsType === "all" ? undefined : newPostsType; // API expects "image" or "video"
      getUserGalleryPostsAPI(profile._id, { page: 1, perPage: 15, postTypes })
        .then(data => {
          if (
            (data as any).message === "This profile is private" ||
            (data as any).message ===
              "This profile is restricted to followers only" ||
            (data as any).message === "Content is hidden. Follow to view."
          ) {
            setProfileAccessMessage((data as any).message);
            setPosts(null);
          } else {
            setProfileAccessMessage(null);
            setPosts({
              ...data,
              items: data.items.map((post: Post) => ({
                ...post,
                content: post.content.map(contentItem => ({
                  ...contentItem,
                  isBlurred:
                    loadId === me?.id || isAlreadySubscribed
                      ? false
                      : contentItem.isBlurred,
                })),
              })),
            });
          }
        })
        .catch(err => {
          showToast(err?.message || "Error loading posts!", "error");
          console.error("Error loading posts:", err);
        });
    }
  };

  const handleCancelSubscription = async () => {
    if (!profile?._id) return;
    try {
      setCancellingSubscription(true);
      await cancelSubscriptionAPI(profile._id);
      setCancelSubscriptionDialogVisible(false);
      setIsAlreadySubscribed(false);
      showToast("Subscription cancelled successfully", "success");
    } catch (err) {
      console.error("Error cancelling subscription:", err);
      showToast(
        (err as any)?.message || "Failed to cancel subscription",
        "error"
      );
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handlePostClick = (post: Post) => {
    const isPaidLocked =
      post.price > 0 &&
      !post.access &&
      !(me?.isAdmin || me?.isSuperAdmin) &&
      !isOwner;

    if (isPaidLocked) {
      if (!me?.id) {
        showToast("You must be logged in to purchase content.", "error");
        return;
      }
      handlePurchasePost(post);
      return;
    }

    setSelectedPost(post);
    setDialogOpen(true);
    history.replace({
      pathname: history.location.pathname,
      search: `?postId=${post._id}`,
      state: {
        fromPostDialog: true,
        previousPath: location.pathname + location.search,
      },
    });
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPost(null);
    setHandledPostId(null);
    history.replace({
      pathname: history.location.pathname,
      search: "",
    });
  };

  const handleMessageClick = async () => {
    if (!profile || !profile._id || !me?.id) {
      showToast("Error: profile could not be found.", "error");
      return;
    }

    // Show loading toast
    showToast("Opening conversation...", "info");

    try {
      const response = await createConversationAPI(profile._id);
      if (response.success && response.data?.conversationId) {
        // Navigate to messages page with the conversation ID
        history.push(
          `/messages?conversationId=${response.data.conversationId}`
        );
        showToast("Conversation opened!", "success");
      } else {
        showToast("Error creating a chat.", "error");
      }
    } catch (err) {
      console.error("Error creating conversation:", err);
      showToast("Error creating a chat.", "error");
    }
  };

  const handleOpenTipDialog = () => {
    const hasAnySubscriptionAccess =
      isSubscribedOrTrial || hasAnyActiveSubscriptionToCreator;

    if (
      profile?.privacySettings?.allowTipsWithoutSubscription === true &&
      !hasAnySubscriptionAccess
    ) {
      showToast("An active subscription is required to send a tip.", "info");
      return;
    }
    setTipDialogVisible(true);
  };

  const handleSubscribe = async () => {
    if (subscribeStep === 0) {
      if (!selectedTier || !profile?._id || !me?.id) {
        showToast("Please select a subscription tier.", "error");
        return;
      }
      setSubscribeStep(1);
      return;
    }
    try {
      const selectedSubscription = profile.subscriptionPrice.find(
        tier => tier.tierName.toLowerCase() === selectedTier.toLowerCase()
      );
      if (selectedSubscription) {
        showToast(
          `Subscribed to ${selectedTier} for $${selectedSubscription.price}!`,
          "success"
        );
        setSubscribeDialogVisible(false);
        setSelectedTier("");
        setSubscribeStep(0);
      } else {
        showToast("Subscription tier not found.", "error");
      }
    } catch (err) {
      showToast("Error subscribing to tier.", "error");
      console.error("Subscribe error:", err);
    }
  };

  const handleTipSuccess = (tipAmount: number) => {
    if (profile) {
      showToast(
        `Tipped $${tipAmount} to ${profileDisplayName}!`,
        "success"
      );
    }
  };

  const handlePurchasePost = (post: Post) => {
    if (!me?.id) {
      showToast("You must be logged in to purchase content.", "error");
      return;
    }
    setSelectedPostForPurchase(post);
    setPurchasePostDialogVisible(true);
  };

  const applyUnlockedPostToProfileState = (post: Post) => {
    setPosts(prev =>
      prev
        ? {
            ...prev,
            items: prev.items.map(p =>
              p._id === post._id
                ? {
                    ...p,
                    access: true,
                    content: p.content.map(c => ({
                      ...c,
                      isBlurred: false,
                    })),
                  }
                : p
            ),
          }
        : prev
    );
    setSelectedPost(prev =>
      prev && prev._id === post._id
        ? {
            ...prev,
            access: true,
            content: prev.content.map(c => ({
              ...c,
              isBlurred: false,
            })),
          }
        : prev
    );
    const label = post.content[0]?.type === "video" ? "video" : "image";
    showToast(`Successfully unlocked ${label}!`, "success");
    setPurchasePostDialogVisible(false);
    setSelectedPostForPurchase(null);
  };

  const handleCompletePurchase = () => {
    if (!selectedPostForPurchase) {
      showToast("No post selected for purchase.", "error");
      return;
    }
    applyUnlockedPostToProfileState(selectedPostForPurchase);
  };

  const handlePurchaseSuccess = () => {
    if (!selectedPostForPurchase) return;
    applyUnlockedPostToProfileState(selectedPostForPurchase);
  };

  // Hashtag management functions (same as PostCreator)
  const addHashtag = (tag: string) => {
    const cleanTag = tag.trim().replace(/^#+/, "").toLowerCase();
    if (
      cleanTag &&
      !editHashtags.includes(cleanTag) &&
      editHashtags.length < 5
    ) {
      setEditHashtags([...editHashtags, cleanTag]);
      setRawHashtagInput("");
    }
  };

  const removeHashtag = (tagToRemove: string) => {
    setEditHashtags(editHashtags.filter(tag => tag !== tagToRemove));
  };
  const removeCategory = (categoryToRemove: string) => {
    setEditCategories(editCategories.filter(category => category !== categoryToRemove));
  };

  const handleHashtagKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "Return") {
      e.preventDefault();
      if (rawHashtagInput.trim()) {
        addHashtag(rawHashtagInput);
      }
    }
  };

  const handleHashtagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRawHashtagInput(value);
  };
  const handleHashtagInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (
      (e.key === "Backspace" || e.key === "Delete") &&
      rawHashtagInput.trim() === "" &&
      editHashtags.length > 0
    ) {
      e.preventDefault();
      setEditHashtags(prev => prev.slice(0, prev.length - 1));
    }
  };
  const handleCategorySelectChange = (
    e: React.ChangeEvent<{ value: unknown }>
  ) => {
    const categoryId = String(e.target.value || "");
    if (!categoryId) return;
    if (
      !editCategories.includes(categoryId) &&
      editCategories.length < 5
    ) {
      setEditCategories([...editCategories, categoryId]);
    }
  };

  const handleEditPost = (post: Post) => {
    // Use selectedPost data if available and it's the same post (for PostDialog)
    const postToEdit =
      selectedPost && selectedPost._id === post._id ? selectedPost : post;

    setSelectedPostForEdit(postToEdit);

    // Fill form with actual post data
    setEditDescription(postToEdit.description || "");

    // Handle hashtags - convert to array of lowercase strings without #
    const hashtags = postToEdit.hashtags || [];
    setEditHashtags(
      hashtags
        .map(tag => {
          const cleaned = tag.replace(/^#+/, "").trim().toLowerCase();
          return cleaned;
        })
        .filter(Boolean)
    );
    setRawHashtagInput("");
    const categories = postToEdit.categories || [];
    setEditCategories(
      categories
        .map(category => {
          const rawCategory =
            typeof category === "string"
              ? category
              : category?._id || category?.name || "";
          return rawCategory.replace(/^#+/, "").trim().toLowerCase();
        })
        .filter(Boolean)
    );

    const initialPrice = clampPostPrice(postToEdit.price || 0);
    setEditPrice(initialPrice);
    setEditPriceInput(formatPostPriceInput(initialPrice));
    const normalizedSubscriptionType =
      postToEdit.subscriptionType === "basic" ||
      postToEdit.subscriptionType === "premium"
        ? postToEdit.subscriptionType
        : "basic";
    setEditSubscriptionType(normalizedSubscriptionType);
    setEditPostDialogVisible(true);
  };

  const handleSavePostEdit = async () => {
    if (!selectedPostForEdit) return;

    // Validation
    if (
      editDescription.trim().length < 1 ||
      editDescription.trim().length > 2000
    ) {
      showToast("Description must be between 1 and 2000 characters", "error");
      return;
    }

    const isPaidPost = isPaidPostType(selectedPostForEdit.type);

    if (isPaidPost && (editPrice < 0 || editPrice > 9999)) {
      showToast("Price must be between 0 and 9999", "error");
      return;
    }

    // Validate minimum price for private posts
    const isPrivatePost = isPaidPost || editPrice > 0;
    if (isPrivatePost && editPrice > 0 && editPrice < 5) {
      showToast("Minimum price for private posts is $5.00", "error");
      return;
    }

    // Hashtags are already in the correct format (array of lowercase strings without #)
    if (editHashtags.length > 5) {
      showToast("Maximum 5 hashtags allowed", "error");
      return;
    }

    setIsSavingPost(true);
    try {
      const payload: {
        description: string;
        hashtags: string[];
        categories: string[];
        subscriptionType: "basic" | "premium";
        price?: number;
      } = {
        description: editDescription.trim(),
        hashtags: editHashtags,
        categories: editCategories,
        subscriptionType: editSubscriptionType,
      };
      if (isPaidPost) {
        payload.price = editPrice;
      }

      const updatedPrice = isPaidPost
        ? editPrice
        : clampPostPrice(selectedPostForEdit.price || 0);

      await updateGalleryPostAPI(selectedPostForEdit._id, {
        ...payload,
      });

      // Optimistic update
      setPosts(prev => {
        if (!prev) return prev;
        const updated = prev.items.map(p =>
          p._id === selectedPostForEdit._id
            ? {
                ...p,
                description: editDescription.trim(),
                hashtags: editHashtags,
                categories: editCategories,
                subscriptionType: editSubscriptionType,
                price: updatedPrice,
              }
            : p
        );
        return { ...prev, items: updated };
      });

      // Update selectedPost if it's the same post (for PostDialog)
      if (selectedPost && selectedPost._id === selectedPostForEdit._id) {
        setSelectedPost({
          ...selectedPost,
          description: editDescription.trim(),
          hashtags: editHashtags,
          categories: editCategories,
          subscriptionType: editSubscriptionType,
          price: updatedPrice,
        });
      }

      // Update selectedPostForEdit to keep it in sync
      setSelectedPostForEdit({
        ...selectedPostForEdit,
        description: editDescription.trim(),
        hashtags: editHashtags,
        categories: editCategories,
        subscriptionType: editSubscriptionType,
        price: updatedPrice,
      });

      showToast("Post updated successfully", "success");
      setEditPostDialogVisible(false);
      setSelectedPostForEdit(null);
      setRawHashtagInput("");
      setEditSubscriptionType("basic");
    } catch (error: any) {
      console.error("Error updating post:", error);
      showToast(
        error?.response?.data?.message || "Failed to update post",
        "error"
      );
    } finally {
      setIsSavingPost(false);
    }
  };

  const handleRequestDeletePost = (post: Post) => {
    // Close Edit Post modal before showing confirm, as per design
    setEditPostDialogVisible(false);
    setSelectedPostForEdit(null);
    setPostPendingDelete(post);
    setDeletePostConfirmOpen(true);
  };
  const handleDeletePost = async (post: Post) => {
    if (!post._id) {
      showToast("Post ID is missing", "error");
      return;
    }

    try {
      await deletePostAPI(post._id);

      // Видаляємо пост зі списку
      setPosts(prev => {
        if (!prev) return prev;
        const updated = prev.items.filter(p => p._id !== post._id);
        return { ...prev, items: updated, totalCount: prev.totalCount - 1 };
      });

      showToast("Post deleted successfully", "success");
    } catch (err: any) {
      showToast(err?.response?.data?.message || "Error deleting post", "error");
      console.error("Delete error:", err);
    }
  };

  const handleSubscriptionClick = async () => {
    try {
      const res: any = await checkUserSubscriptionAPI(creatorId);
      const isActive = Boolean(res?.isActive ?? res?.isSubscribed);
      setIsTrialAvailable(Boolean(res?.isTrialAvailable));
      setTrialUsed(Boolean(res?.trialUsed));
      setSubscriptionEndDate(
        typeof res?.endDate === "string" ? res.endDate : null
      );
      setCurrentSubscriptionTier(
        typeof res?.tier === "string" ? res.tier.toLowerCase() : null
      );

      if (isActive) {
        if (res?.cancelledAt) {
          setPendingCancelModalVisible(true);
        } else {
          setCancelSubscriptionDialogVisible(true);
        }
      } else {
        openSubscribeDialogWithLoader();
      }
    } catch (e) {
      console.error("❌ Failed to fetch subscription status", e);
    }
  };
  const handleSubscribeButtonClick = async (
    e?: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (isSubscribedOrTrial) {
      if (e?.currentTarget) {
        setSubscriptionMenuEl(e.currentTarget as HTMLElement);
      }
      return;
    }
    if (shouldInstantFreeSubscribe) {
      await handleSubscribeFree();
      return;
    }
    await handleSubscriptionClick();
  };

  function getAgeFromDOB(dob: string): number {
    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }

  const isSafeHref = (href: string) => /^(https?:\/\/|mailto:)/i.test(href);
  const prettyUrl = (href: string) =>
    href.replace(/^https?:\/\//i, "").replace(/^mailto:/i, "");

  const creatorSubscriptionTierLc =
    profile?.activeSubscription?.tier?.toLowerCase() ?? "";

  const isBannerShow =
    profile?.activeSubscription &&
    (creatorSubscriptionTierLc === "diamond" ||
      creatorSubscriptionTierLc === "platinum_vip");

  /** Золота обводка аватара: VIP-банер (diamond / platinum_vip) або tier `premium` */
  const isPremiumAvatarBorder =
    isBannerShow || creatorSubscriptionTierLc === "premium";

  /** Зсув аватара на банер: моб. 5%; ≥1440px −10%−30px; десктоп до 1440px −20%−30px */
  const profileHeaderAvatarTransform = isMobile
    ? "translateY(calc(5% - 30px))"
    : isWideDesktop
      ? "translateY(calc(-10% - 30px))"
      : "translateY(calc(-20% - 30px))";

  // Helper function to get absolute URL for meta tags
  const getAbsoluteImageUrl = (url: string | undefined): string => {
    // If no URL provided, use defaultAvatars
    if (!url) {
      const defaultUrl = defaultAvatars;
      // If already absolute (http/https/blob/data), return as is
      if (
        defaultUrl.startsWith("http") ||
        defaultUrl.startsWith("blob:") ||
        defaultUrl.startsWith("data:")
      ) {
        return defaultUrl;
      }
      // If relative path (starts with /), make it absolute
      return defaultUrl.startsWith("/")
        ? `${window.location.origin}${defaultUrl}`
        : `${window.location.origin}/${defaultUrl}`;
    }

    // If already absolute (http/https/blob/data), return as is
    if (
      url.startsWith("http") ||
      url.startsWith("blob:") ||
      url.startsWith("data:")
    ) {
      return url;
    }

    // If relative path (starts with /), make it absolute
    return url.startsWith("/")
      ? `${window.location.origin}${url}`
      : `${window.location.origin}/${url}`;
  };

  const ogImageUrl = getAbsoluteImageUrl(avatarImageSrc);
  const twitterImageUrl = getAbsoluteImageUrl(avatarImageSrc);
  const robotsContent = (() => {
    // Prevent indexing while profile is unresolved/loading.
    if (!profile) {
      return "noindex,nofollow";
    }

    // Private/restricted profile variants should not be indexed.
    if (profile.private || lockedByPrivate) {
      return "noindex,nofollow";
    }

    // Creator profile that is not validated yet should not be indexed.
    if (profile.userType?.title === "creator" && !profile.validated) {
      return "noindex,follow";
    }

    return "index,follow";
  })();

  return (
    <>
      <Helmet>
        <title>{dynamicSEO?.title || seo.title}</title>
        <meta
          name="description"
          content={dynamicSEO?.description || seo.description}
        />
        <link
          rel="canonical"
          href={buildCanonicalUrl(window.location.href, {
            dropTrackingParams: true,
          })}
        />
        <meta name="robots" content={robotsContent} />
        <meta name="googlebot" content={robotsContent} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="profile" />
        <meta
          property="og:title"
          content={dynamicSEO?.title || seo.title || ""}
        />
        <meta
          property="og:description"
          content={dynamicSEO?.description || seo.description || ""}
        />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:site_name" content="Sugar Cribs" />
        <meta property="og:image" content={ogImageUrl} />

        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content={
            profile?.firstName && profile?.lastName
              ? `${profile.firstName} ${profile.lastName} profile picture`
              : "Sugar Cribs profile picture"
          }
        />

        {/* Profile specific OG tags */}
        {/* {profile && (
          <>
            <meta property="profile:first_name" content={profile.firstName} />
            <meta property="profile:last_name" content={profile.lastName} />
            <meta property="profile:username" content={profile.username} />
          </>
        )} */}

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={dynamicSEO?.title || seo.title || ""}
        />
        <meta
          name="twitter:description"
          content={dynamicSEO?.description || seo.description || ""}
        />
        <meta name="twitter:image" content={twitterImageUrl} />
        <meta
          name="twitter:image:alt"
          content={
            profile?.firstName && profile?.lastName
              ? `${profile.firstName} ${profile.lastName} profile picture`
              : "Sugar Cribs profile picture"
          }
        />

        {dynamicSEO?.jsonLd && !isSearchIndexingBlocked() && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: serializeJsonLdForHtmlScript(dynamicSEO.jsonLd),
            }}
          />
        )}
      </Helmet>
      <div className={classes.root}>
        {/*  */}
        {/* {(history.length > 1 || (location.state as any)?.fromPostDialog) && (
          <Button
            onClick={handleGoBack}
            startIcon={<ArrowLeft size={24} />}
            style={{ color: "white", marginBottom: "8px" }}
          >
            Back
          </Button>
        )} */}
        {profile && (
          <Box className={classes.breadcrumbs}>
            <Typography
              component={RouterLink}
              to="/"
              className={classes.breadcrumbHome}
            >
              Home
            </Typography>
            <Typography className={classes.breadcrumbSeparator}>/</Typography>
            <Typography className={classes.breadcrumbActive}>
              {profile.username}
            </Typography>
          </Box>
        )}
        {/* {profile?.activeSubscription && isBannerShow && ( */}
        <div ref={profileBannerRef} data-guide-step="profile-banner">
          <ProfileBanner
            profileBannerImage={bannerImageSrc}
            avatarImage={avatarImageSrc}
            isOwner={isOwner}
            isLoading={bannerLoadingState}
            onBannerSelect={handleBannerSelected}
            showEditButton={isOwner}
          />
        </div>
        {isOwner && (
          <>
            <input
              ref={bannerInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleBannerFileChange}
            />
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleAvatarFileChange}
            />
          </>
        )}
        {/* )} */}
        <div className={classes.profileHeader}>
          {isMobile ? (
            <>
              <div className={classes.headerTop} style={{ height: 60 }}>
                <div
                  onClick={() => history.push(`/${profile.username}`)}
                  style={{
                    cursor: "pointer",
                    position: "relative",
                    zIndex: 2,
                    transform: profileHeaderAvatarTransform,
                    padding: isBannerShow ? 5 : 0,
                    background: isBannerShow ? "#000" : "transparent",
                    borderRadius: 9999,
                  }}
                >
                  <div className={classes.avatarWrapper}>
                    <Avatar
                      src={displayAvatarSrc}
                      className={classes.avatar}
                      style={{
                        width: 108,
                        height: 108,
                        border: isBannerShow ? "6px solid #F6EF89" : "none",
                      }}
                    >
                      {!pendingAvatarPreviewUrl &&
                        profile?.profileImg &&
                        loadingMedia[profile.profileImg] && (
                          <CircularProgress size={24} />
                        )}
                    </Avatar>
                    {isOwner && (
                      <button
                        type="button"
                        className={classes.avatarEditOverlay}
                        onClick={handleOpenAvatarFileDialog}
                        aria-label="Change profile photo"
                      >
                        <img
                          src={cameraOverlayIcon}
                          alt="Change profile"
                          className={classes.avatarEditIcon}
                        />
                      </button>
                    )}
                  </div>
                </div>
                <div
                  className={[
                    classes.userInfo,
                    !isOwner && classes.userInfoWhenNotOwner,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <div className={classes.userName}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <div className={classes.nameContainer}>
                        <Typography
                          className={classes.firstLastName}
                          onClick={() => history.push(`/${profile.username}`)}
                          style={{ cursor: "pointer", fontSize: "16px" }}
                        >
                          {profileDisplayName}{" "}
                          <VerifiedShieldIcon
                            className={classes.verifiedIcon}
                          />
                        </Typography>
                        <Typography
                          onClick={() => history.push(`/${profile.username}`)}
                          style={{
                            fontSize: "12px",
                            fontWeight: "bold",
                            color: "#ED9CB7",
                            cursor: "pointer",
                          }}
                        >
                          @{profile.username}
                        </Typography>
                      </div>

                      <div
                        ref={tipChatButtonsRef}
                        style={{
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                          gap: 16,
                        }}
                      >
                        <GuideSteps
                          steps={[profileGuideStep2]}
                          pageType="profile"
                          stepOffset={3}
                        />
                        {!isOwner && !isProfileViewer && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 16,
                            }}
                          >
                            <button
                              type="button"
                              style={{
                                width: 28,
                                height: 28,
                              }}
                              className={classes.buttonActionIcon}
                              onClick={handleMessageClick}
                              data-guide-step="profile-message"
                              aria-label="Open chat"
                            >
                              <ChatProfileIcon />
                            </button>
                            <button
                              type="button"
                              style={{
                                width: 28,
                                height: 28,
                              }}
                              className={classes.buttonActionIcon}
                              onClick={() => {
                                handleOpenTipDialog();
                              }}
                              data-guide-step="profile-message"
                              aria-label="Tip creator"
                            >
                              <ProfileDollarIcon />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {isOwner && hasPendingProfileMediaChanges && (
                      <div className={classes.profileEditActions}>
                        <Button
                          className={classes.profileActionButton}
                          onClick={handleCancelPendingMediaChanges}
                          disabled={saveAvatarMutation.isPending}
                        >
                          <span>Cancel</span>
                        </Button>
                        <Button
                          className={classes.profileActionButton}
                          onClick={handleSavePendingAvatar}
                          disabled={saveAvatarMutation.isPending}
                        >
                          <span>
                            {saveAvatarMutation.isPending ? "Saving..." : "Save changes"}
                          </span>
                        </Button>
                      </div>
                    )}

                    {isOwner && (
                      <div className={classes.creatorMeta}>
                        <div className={classes.stats}>
                          <Typography
                            style={{
                              fontSize: "16px",
                              fontWeight: 500,
                              color: "#fff",
                              paddingRight: 20,
                              borderRight: "1px solid #FFFFFF1A",
                            }}
                          >
                            <p
                              style={{
                                fontSize: "10px",
                                marginBottom: 3,
                                fontWeight: 400,
                                color: "#AEAEAE",
                              }}
                            >
                              Subscribers
                            </p>
                            {profile.subscribeCount?.toLocaleString() ?? 0}
                          </Typography>
                          <Typography
                            style={{
                              fontSize: "16px",
                              fontWeight: 500,
                              color: "#fff",
                              paddingRight: 20,
                            }}
                          >
                            <p
                              style={{
                                fontSize: "10px",
                                marginBottom: 3,
                                fontWeight: 400,
                                color: "#AEAEAE",
                              }}
                            >
                              Posts
                            </p>
                            {profile.postsCount?.toLocaleString() || 0}
                          </Typography>
                        </div>
                      </div>
                    )}
                    {/* <div className={classes.stats}>
                    <Typography
                      style={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#FFFFFF",
                      }}
                    >
                      {profile.postsCount?.toLocaleString() || 0} posts
                    </Typography>
                    <Typography
                      style={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#FFFFFF",
                      }}
                    >
                      {profile.followersCount?.toLocaleString() || 0} followers
                    </Typography>
                  </div> */}
                    {/* <div className={classes.nameContainerAge}>
                    <Typography
                      style={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#FFFFFF",
                      }}
                    >
                      Age: {getAgeFromDOB(profile.DOB)}
                    </Typography>
                    <Typography
                      style={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#FFFFFF",
                      }}
                    >
                      {profile.gender}
                    </Typography>
                  </div>

                  <div className={classes.nameContainer}>
                    <Typography
                      style={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#FFFFFF",
                      }}
                    >
                      {profile.location}
                    </Typography>
                  </div>
                  {isProfileViewer && (
                    <div className={classes.nameContainerAge}>
                      <Typography
                        style={{
                          fontSize: "14px",
                          fontWeight: 400,
                          color: "#FFFFFF",
                        }}
                      >
                        {profile.annualIncome}
                      </Typography>
                    </div>
                  )} */}

                    <div
                      ref={actionButtonsRef}
                      style={{
                        width: "100%",
                        position: "relative",
                      }}
                      className={classes.actionButtons}
                    >
                      <GuideSteps
                        steps={[profileGuideStep1]}
                        pageType="profile"
                        stepOffset={2}
                      />
                      {!isOwner && !isProfileViewer && (
                        <Button
                          className={
                            isSubscribedOrTrial
                              ? classes.subscribeButtonSubscribed
                              : shouldInstantFreeSubscribe
                                ? classes.subscribeButtonSingleFree
                                : classes.subscribeButton
                          }
                          startIcon={
                            !isMobile &&
                            !isSubscribedOrTrial &&
                            !shouldInstantFreeSubscribe ? (
                              <NotificationIcon />
                            ) : undefined
                          }
                          onClick={handleSubscribeButtonClick}
                          disabled={checkingSubscription}
                        >
                          {checkingSubscription ? (
                            <CircularProgress
                              size={16}
                              style={{ color: "#FFFFFF" }}
                            />
                          ) : (
                            <Typography
                              className={classes.subscribeButtonText}
                              style={
                                shouldInstantFreeSubscribe
                                  ? { color: "#BE3265" }
                                  : undefined
                              }
                            >
                              {isSubscribedOrTrial ? (
                                "Subscribed"
                              ) : (
                                <span
                                  className={classes.subscribeButtonTextInner}
                                >
                                  {!shouldInstantFreeSubscribe && (
                                    <ProfileDollarIcon />
                                  )}
                                  Subscribe
                                </span>
                              )}
                            </Typography>
                          )}
                        </Button>
                      )}
                      <Button
                        className={[
                          classes.shareButton,
                          isOwner && classes.shareButtonHiddenOnOwnProfile,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={handleOpenSharePopup}
                        aria-label="Share profile"
                      >
                        <img src={shareProfileIcon} alt="Share" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <div
                style={{
                  marginTop: isBannerShow ? 15 : 10,
                }}
                className={classes.headerBottom}
              >
                {/* <div className={classes.actionButtons}>
                  {!isOwner && (
                    <Button
                      size="small"
                      className={classes.buttonAction}
                      startIcon={<ChatSupportIcon />}
                      onClick={handleMessageClick}
                    >
                      <Typography className={classes.nameButSup}>
                        Message
                      </Typography>
                    </Button>
                  )}
                  {!isOwner && !isProfileViewer && (
                    <Button
                      className={classes.buttonAction}
                      startIcon={<NotificationIcon />}
                      onClick={async () => {
                        if (isAlreadySubscribed) {
                          try {
                            const res: any = await checkUserSubscriptionAPI(creatorId);
                            const isActive = Boolean(res?.isActive ?? res?.isSubscribed);
                            setIsAlreadySubscribed(isActive);
                            setSubscriptionEndDate(
                              typeof res?.endDate === "string" ? res.endDate : null
                            );
                            setIsTrialAvailable(Boolean(res?.isTrialAvailable));
                            setTrialUsed(Boolean(res?.trialUsed));
                            setCurrentSubscriptionTier(
                              typeof res?.tier === "string"
                                ? res.tier.toLowerCase()
                                : null
                            );
                            setCancelSubscriptionDialogVisible(true);
                          } catch (e) {
                            console.error(
                              "❌ Failed to fetch subscription status",
                              e
                            );
                          }
                        } else {
                          setSubscribeDialogVisible(true);
                          setSubscribeStep(0);
                          setSelectedTier("Premium");
                        }
                      }}
                      disabled={checkingSubscription}
                    >
                      {checkingSubscription ? (
                        <CircularProgress
                          size={16}
                          style={{ color: "#FFFFFF" }}
                        />
                      ) : isAlreadySubscribed ? (
                        <Typography className={classes.nameButSup}>
                          Subscribed
                        </Typography>
                      ) : (
                        <Typography className={classes.nameButSup}>
                          Subscribe
                        </Typography>
                      )}
                    </Button>
                  )}
                  {!isOwner && !isProfileViewer && (
                    <Button
                      className={classes.buttonAction}
                      startIcon={<DollarIcon />}
                      onClick={() => {
                        handleOpenTipDialog();
                        setTipStep(0);
                        setTipAmount(0);
                      }}
                    >
                      <Typography className={classes.nameButSup}>
                        Tip Creator
                      </Typography>
                    </Button>
                  )}
                </div> */}
                <div className={classes.creatorMeta}>
                  {/* <Button
                    variant="outlined"
                    className={classes.copyButton}
                    onClick={handleShareClick}
                  >
                    <OverlappingRectsIcon style={{ marginRight: 8 }} />
                    Share
                  </Button> */}
                  {isMobile && (
                    <div className={classes.nameContainerAge}>
                      <Typography
                        style={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "#AEAEAE",
                          marginBottom: "0px",
                        }}
                      >
                        About
                      </Typography>
                      <Box
                        display="flex"
                        flexDirection="row"
                        style={{ gap: 8 }}
                      >
                        {profile.DOB && (
                          <Box
                            display="flex"
                            alignItems="center"
                            style={{ gap: 8 }}
                          >
                            <img
                              src={torteaIcon}
                              alt="age"
                              style={{
                                width: 14,
                                height: 14,
                                objectFit: "contain",
                              }}
                            />
                            <Typography
                              style={{
                                fontSize: "12px",
                                fontWeight: 400,
                                color: "#FFFFFF",
                              }}
                            >
                              Age: {getAgeFromDOB(profile.DOB)} years
                            </Typography>
                          </Box>
                        )}
                        {profile.gender && (
                          <Box
                            display="flex"
                            alignItems="center"
                            style={{ gap: 8 }}
                          >
                            <img
                              src={genderHeartIcon}
                              alt="gender"
                              style={{
                                width: 14,
                                height: 14,
                                objectFit: "contain",
                              }}
                            />
                            <Typography
                              style={{
                                fontSize: "12px",
                                fontWeight: 400,
                                color: "#FFFFFF",
                              }}
                            >
                              Gender: {profile.gender}
                            </Typography>
                          </Box>
                        )}
                        {profile.location && (
                          <Box
                            display="flex"
                            alignItems="center"
                            style={{ gap: 8 }}
                          >
                            <img
                              src={locationIcon}
                              alt="location"
                              style={{
                                width: 14,
                                height: 14,
                                objectFit: "contain",
                              }}
                            />
                            <Typography
                              style={{
                                fontSize: "12px",
                                fontWeight: 400,
                                color: "#FFFFFF",
                              }}
                            >
                              Location: {profile.location}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </div>
                  )}
                  {profile.description && (
                    <Typography className={classes.description}>
                      {profile.description}
                    </Typography>
                  )}

                  {profile.website && isSafeHref(profile.website) && (
                    <a
                      href={profile.website}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className={classes.website}
                      onClick={() =>
                        profile.website && handleLinkClick("website")
                      }
                    >
                      {prettyUrl(profile.website)}
                    </a>
                  )}

                  {profile.link && (
                    <a
                      href={profile.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.link}
                      onClick={() => profile.link && handleLinkClick("link")}
                    >
                      {profile.link}
                    </a>
                  )}
                </div>
              </div>
            </>
          ) : (
            <Box
              style={{ width: "100%" }}
              display="flex"
              flexDirection="column"
            >
              <Box
                display="flex"
                alignItems="center"
                style={{
                  height: 150,
                  gap: "27px",
                  marginTop: isBannerShow ? 0 : 50,
                  alignItems: isBannerShow ? "flex-start" : "center",
                }}
              >
                <div
                  onClick={() => history.push(`/${profile.username}`)}
                  style={{
                    cursor: "pointer",
                    position: "relative",
                    zIndex: 2,
                    transform: profileHeaderAvatarTransform,
                    padding: 0,
                    background: "transparent",
                    borderRadius: 9999,
                  }}
                >
                  <div className={classes.avatarWrapper}>
                    <Avatar
                      src={displayAvatarSrc}
                      className={classes.avatar}
                      style={{
                        boxSizing: "border-box",
                        border: isPremiumAvatarBorder
                          ? "7px solid #F6EF89"
                          : "7px solid #0C0A09",
                      }}
                    >
                      {!pendingAvatarPreviewUrl &&
                        profile?.profileImg &&
                        loadingMedia[profile.profileImg] && (
                          <CircularProgress size={24} />
                        )}
                    </Avatar>
                    {isOwner && (
                      <button
                        type="button"
                        className={classes.avatarEditOverlay}
                        onClick={handleOpenAvatarFileDialog}
                        aria-label="Change profile photo"
                      >
                        <img
                          src={cameraOverlayIcon}
                          alt="Change profile"
                          className={classes.avatarEditIcon}
                        />
                      </button>
                    )}
                  </div>
                </div>
                <div
                  className={[
                    classes.userInfo,
                    !isOwner && classes.userInfoWhenNotOwner,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <div className={classes.userName}>
                    <div className={classes.nameContainer}>
                      <Typography
                        className={classes.firstLastName}
                        onClick={() => history.push(`/${profile.username}`)}
                        style={{
                          cursor: "pointer",
                          fontFamily: "TT Norms, sans-serif",
                        }}
                      >
                        {profileDisplayName}{" "}
                        <VerifiedShieldIcon className={classes.verifiedIcon} />
                      </Typography>
                      <Typography
                        onClick={() => history.push(`/${profile.username}`)}
                        style={{
                          fontSize: "16px",
                          fontWeight: "bold",
                          color: "#ED9CB7",
                          cursor: "pointer",
                        }}
                      >
                        @{profile.username}
                      </Typography>
                    </div>
                    {isOwner && hasPendingProfileMediaChanges && (
                      <div className={classes.profileEditActions}>
                        <Button
                          className={classes.profileActionButton}
                          onClick={handleCancelPendingMediaChanges}
                          disabled={saveAvatarMutation.isPending}
                        >
                          <span>Cancel</span>
                        </Button>
                        <Button
                          className={classes.profileActionButton}
                          onClick={handleSavePendingAvatar}
                          disabled={saveAvatarMutation.isPending}
                        >
                          <span>
                            {saveAvatarMutation.isPending ? "Saving..." : "Save changes"}
                          </span>
                        </Button>
                      </div>
                    )}

                    {!isOwner && (
                      <div
                        ref={tipChatButtonsRef}
                        style={{
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                          gap: 16,
                        }}
                      >
                        <GuideSteps
                          steps={[profileGuideStep2]}
                          pageType="profile"
                          stepOffset={3}
                        />
                        {!isProfileViewer && (
                          <>
                            <button
                              type="button"
                              className={classes.buttonActionIcon}
                              onClick={handleMessageClick}
                              data-guide-step="profile-message"
                              aria-label="Open chat"
                            >
                              <ChatProfileIcon />
                            </button>
                            <button
                              type="button"
                              className={classes.buttonActionIcon}
                              onClick={() => {
                                handleOpenTipDialog();
                              }}
                              data-guide-step="profile-message"
                              aria-label="Tip creator"
                            >
                              <ProfileDollarIcon />
                            </button>
                          </>
                        )}
                        <EllipsisMenuButtonProfile
                          profileId={profile._id}
                          typeReport="profile"
                        />
                      </div>
                    )}
                  </div>
                  <div className={classes.creatorMeta}>
                    {isOwner && (
                      <div className={classes.stats}>
                        <Typography
                          style={{
                            fontSize: "28px",
                            fontWeight: 500,
                            color: "#fff",
                            width: 180,
                            borderRight: "1px solid #FFFFFF1A",
                          }}
                        >
                          <p
                            style={{
                              fontSize: "16px",
                              marginBottom: 3,
                              fontWeight: 400,
                              color: "#AEAEAE",
                            }}
                          >
                            Subscribers
                          </p>
                          {profile.subscribeCount?.toLocaleString() ?? 0}
                        </Typography>
                        <Typography
                          style={{
                            fontSize: "28px",
                            fontWeight: 500,
                            color: "#fff",
                            width: 180,
                          }}
                        >
                          <p
                            style={{
                              fontSize: "16px",
                              marginBottom: 3,
                              fontWeight: 400,
                              color: "#AEAEAE",
                            }}
                          >
                            Posts
                          </p>
                          {profile.postsCount?.toLocaleString() || 0}
                        </Typography>
                      </div>
                    )}

                    <div
                      ref={actionButtonsRef}
                      className={classes.actionButtons}
                      style={{ position: "relative" }}
                    >
                      <GuideSteps
                        steps={[profileGuideStep1]}
                        pageType="profile"
                        stepOffset={2}
                      />
                      {!isOwner && !isProfileViewer && (
                        <Button
                          className={
                            isSubscribedOrTrial
                              ? classes.subscribeButtonSubscribed
                              : shouldInstantFreeSubscribe
                                ? classes.subscribeButtonSingleFree
                                : classes.subscribeButton
                          }
                          endIcon={
                            isSubscribedOrTrial ? (
                              <ChevronDown size={16} />
                            ) : undefined
                          }
                          onClick={handleSubscribeButtonClick}
                          disabled={checkingSubscription}
                        >
                          {checkingSubscription ? (
                            <CircularProgress
                              size={16}
                              style={{ color: "#FFFFFF" }}
                            />
                          ) : isSubscribedOrTrial ? (
                            <Typography
                              className={classes.subscribeButtonText}
                              style={
                                shouldInstantFreeSubscribe
                                  ? { color: "#BE3265" }
                                  : undefined
                              }
                            >
                              Subscribed
                            </Typography>
                          ) : (
                            <Typography
                              className={classes.subscribeButtonText}
                              style={
                                shouldInstantFreeSubscribe
                                  ? { color: "#BE3265" }
                                  : undefined
                              }
                            >
                              <span
                                className={classes.subscribeButtonTextInner}
                              >
                                {!shouldInstantFreeSubscribe && (
                                  <ProfileDollarIcon />
                                )}
                                Subscribe
                              </span>
                            </Typography>
                          )}
                        </Button>
                      )}
                      <Button
                        className={[
                          classes.shareButton,
                          isOwner && classes.shareButtonHiddenOnOwnProfile,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={handleOpenSharePopup}
                        aria-label="Share profile"
                      >
                        <img src={shareProfileIcon} alt="Share" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Box>

              <div className={classes.nameContainerAge}>
                <Typography
                  style={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#AEAEAE",
                  }}
                >
                  About
                </Typography>

                <Box display="flex" style={{ gap: 24 }}>
                  {profile.DOB && (
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <img
                        src={torteaIcon}
                        alt="age"
                        style={{ width: 16, height: 16, objectFit: "contain" }}
                      />
                      <Typography
                        style={{
                          fontSize: "14px",
                          fontWeight: 400,
                          color: "#FFFFFF",
                        }}
                      >
                        {getAgeFromDOB(profile.DOB)} years
                      </Typography>
                    </Box>
                  )}
                  {profile.gender && (
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <img
                        src={genderHeartIcon}
                        alt="gender"
                        style={{ width: 16, height: 16, objectFit: "contain" }}
                      />
                      <Typography
                        style={{
                          fontSize: "14px",
                          fontWeight: 400,
                          color: "#FFFFFF",
                        }}
                      >
                        {profile.gender}
                      </Typography>
                    </Box>
                  )}

                  {isProfileViewer && (
                    <div className={classes.nameContainerAge}>
                      <Typography
                        style={{
                          fontSize: "14px",
                          fontWeight: 400,
                          color: "#FFFFFF",
                        }}
                      >
                        {profile.annualIncome}
                      </Typography>
                    </div>
                  )}

                  {profile.location && (
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <img
                        src={locationIcon}
                        alt="location"
                        style={{ width: 16, height: 16, objectFit: "contain" }}
                      />
                      <Typography
                        style={{
                          fontSize: "14px",
                          fontWeight: 400,
                          color: "#FFFFFF",
                        }}
                      >
                        {profile.location}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </div>

              {profile.description && (
                <Typography className={classes.description}>
                  {profile.description}
                </Typography>
              )}
              {profile.website && isSafeHref(profile.website) && (
                <a
                  href={profile.website}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className={classes.website}
                  onClick={() => profile.website && handleLinkClick("website")}
                >
                  {prettyUrl(profile.website)}
                </a>
              )}

              {profile.link && (
                <a
                  href={profile.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.link}
                  onClick={() => profile.link && handleLinkClick("link")}
                >
                  {profile.link}
                </a>
              )}
            </Box>
          )}
        </div>
        <div ref={profileTabsRef} data-guide-step="profile-tabs">
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            className={classes.tabs}
            classes={{ indicator: classes.indicator }}
            centered
            TabIndicatorProps={{
              style: {
                backgroundColor: "#F05D94",
                height: 6,
                borderRadius: 0,
                top: 0,
                bottom: "auto",
              },
            }}
          >
            <Tab
              icon={
                <span
                  className={classes.tabBarIcon}
                  style={{
                    WebkitMaskImage: `url(${profileTabPostIcon})`,
                    maskImage: `url(${profileTabPostIcon})`,
                  }}
                  aria-hidden
                />
              }
              label="POSTS"
              className={classes.tab}
            />
            <Tab
              icon={
                <span
                  className={classes.tabBarIcon}
                  style={{
                    WebkitMaskImage: `url(${profileTabPhotosIcon})`,
                    maskImage: `url(${profileTabPhotosIcon})`,
                  }}
                  aria-hidden
                />
              }
              label="PHOTO"
              className={classes.tab}
            />
            <Tab
              icon={
                <span
                  className={classes.tabBarIcon}
                  style={{
                    WebkitMaskImage: `url(${profileTabPlayIcon})`,
                    maskImage: `url(${profileTabPlayIcon})`,
                  }}
                  aria-hidden
                />
              }
              label="VIDEOS"
              className={classes.tab}
            />
            {variant === "creator" && (
              <Tab
                icon={
                  <span
                    className={classes.tabBarIcon}
                    style={{
                      WebkitMaskImage: `url(${profileTabVideoIcon})`,
                      maskImage: `url(${profileTabVideoIcon})`,
                    }}
                    aria-hidden
                  />
                }
                label={isMobile ? "CUSTOM" : "CUSTOM VIDEOS"}
                className={classes.tab}
              />
            )}
          </Tabs>
        </div>
        {postsType === "custom videos" ? (
          <>
            <Box
              style={{
                marginTop: "20px",
                marginBottom: "16px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              <Typography
                style={{
                  color: "white",
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: 700,
                  marginBottom: "12px",
                }}
              >
                Get a custom video from this creator — made just for you
              </Typography>
              <Typography
                style={{
                  color: "white",
                  fontSize: "16px",
                  fontWeight: 500,
                  marginBottom: "8px",
                }}
              >
                Video price: ${profile?.customContentPrice || 100}
              </Typography>
              <Typography
                style={{
                  color: "#FFFFFFCC",
                  fontSize: "14px",
                  marginBottom: "16px",
                  textAlign: "center",
                  maxWidth: "500px",
                }}
              >
                All custom videos must be delivered within 7 business days of
                order confirmation.
              </Typography>
              <Button
                className={classes.requestCustomVideoBtn}
                onClick={async () => {
                  if (!profile?.username) {
                    setRequestVideoDialog(true);
                    return;
                  }
                  try {
                    const response = await getLastVideoRequestByUsernameAPI(
                      profile.username
                    );
                    const videoRequest = response?.data?.videoRequest || null;
                    if (!videoRequest) {
                      setRequestVideoDialog(true);
                      return;
                    }
                    setLastVideoRequest(videoRequest);
                    setVideoRequestInfoOpen(true);
                  } catch (error) {
                    console.error("Error fetching last video request", error);
                    setRequestVideoDialog(true);
                  }
                }}
                style={{ marginLeft: 0 }}
              >
                Request a video
              </Button>
            </Box>
            <Modal
              open={requestVideoDialog}
              onClose={() => setRequestVideoDialog(false)}
              disablePortal
              disableEnforceFocus
              disableScrollLock
              className={classes.modalSubscription}
            >
              <Box
                className={classes.modelSubscriptionWrapper}
                style={{ display: requestVideoDialog ? "block" : "none" }}
              >
                <CustomVideoRequestDialog
                  onClose={() => setRequestVideoDialog(false)}
                  onSubmit={(data: any) => {
                    sendVideoRequestAPI({
                      userId: me?.id,
                      modelId: id,
                      ...data,
                    })
                      .then(() => {
                        showToast("Video request sent successfully!");
                        setRequestVideoDialog(false);
                      })
                      .catch((e: any) => {
                        const errorMessage =
                          e?.response?.data?.message ||
                          e?.message ||
                          "Error sending video request!";
                        showToast(errorMessage, "error");
                      });
                  }}
                  price={profile?.customContentPrice || 100}
                  modelId={id}
                />
              </Box>
            </Modal>

            <Modal
              open={videoRequestInfoOpen}
              onClose={() => {
                setVideoRequestInfoOpen(false);
                setIsVideoExpanded(false);
                setIsVideoPlaying(false);
              }}
              disablePortal
              disableEnforceFocus
              disableScrollLock
              className={classes.modalSubscription}
            >
              <Box className={classes.modelSubscriptionWrapper}>
                <Box
                  bgcolor="#131313"
                  color="#fff"
                  padding={3}
                  position="relative"
                  style={{
                    borderRadius: "16px",
                    maxWidth: isVideoExpanded ? 800 : 520,
                    margin: "0 auto",
                  }}
                >
                  {!(lastVideoRequest?.videoUrl && isVideoExpanded) && (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={2}
                    >
                      <Typography
                        style={{
                          fontSize: isMobile ? "20px" : "32px",
                          fontWeight: 700,
                          color: "#FFFFFF",
                        }}
                      >
                        {lastVideoRequest?.status === "completed" ||
                        lastVideoRequest?.status === "accepted"
                          ? "Video request completed"
                          : "Video request in progress"}
                      </Typography>
                      <IconButton
                        onClick={() => setVideoRequestInfoOpen(false)}
                        style={{ color: "#FFFFFF" }}
                      >
                        <X size={20} />
                      </IconButton>
                    </Box>
                  )}

                  {lastVideoRequest?.status === "completed" ||
                  lastVideoRequest?.status === "accepted" ? (
                    lastVideoRequest?.videoUrl && isVideoExpanded ? (
                      <Box
                        position="relative"
                        width="100%"
                        height="100%"
                        minHeight={isMobile ? 270 : 400}
                        onMouseEnter={() => setIsVideoHovered(true)}
                        onMouseLeave={() => setIsVideoHovered(false)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#000",
                          borderRadius: "12px",
                        }}
                      >
                        <video
                          ref={el => {
                            videoInPopupRef.current = el;
                          }}
                          onPlay={() => setIsVideoPlaying(true)}
                          onPause={() => setIsVideoPlaying(false)}
                          onClick={() => {
                            if (videoInPopupRef.current) {
                              if (isVideoPlaying) {
                                videoInPopupRef.current.pause();
                              } else {
                                videoInPopupRef.current.play();
                              }
                            }
                          }}
                          style={{
                            width: "100%",
                            height: "100%",
                            maxHeight: "80vh",
                            minHeight: isMobile ? 270 : 400,
                            objectFit: "contain",
                            cursor: "pointer",
                          }}
                          src={lastVideoRequest.videoUrl}
                        />
                        {!isVideoPlaying && (
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              videoInPopupRef.current?.play();
                            }}
                            style={{
                              position: "absolute",
                              left: "50%",
                              top: "50%",
                              transform: "translate(-50%, -50%)",
                              width: 48,
                              height: 48,
                              borderRadius: "50%",
                              backgroundColor: "#FFFFFF33",
                              backdropFilter: "blur(3.38px)",
                              WebkitBackdropFilter: "blur(3.38px)",
                              border: "none",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: 0,
                            }}
                          >
                            <img
                              src={playButtonIcon}
                              alt="Play"
                              style={{ width: 34, height: 34 }}
                            />
                          </button>
                        )}
                        {isVideoPlaying && isVideoHovered && (
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              videoInPopupRef.current?.pause();
                            }}
                            style={{
                              position: "absolute",
                              left: "50%",
                              top: "50%",
                              transform: "translate(-50%, -50%)",
                              width: 48,
                              height: 48,
                              borderRadius: "50%",
                              backgroundColor: "#FFFFFF33",
                              backdropFilter: "blur(3.38px)",
                              WebkitBackdropFilter: "blur(3.38px)",
                              border: "none",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: 0,
                            }}
                          >
                            <Pause size={24} color="#FFFFFF" />
                          </button>
                        )}
                        <IconButton
                          onClick={() => {
                            setIsVideoExpanded(false);
                            setIsVideoPlaying(false);
                            videoInPopupRef.current?.pause();
                          }}
                          style={{
                            position: "absolute",
                            right: 16,
                            bottom: 16,
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            backgroundColor: "#FFFFFF33",
                            backdropFilter: "blur(3.38px)",
                            WebkitBackdropFilter: "blur(3.38px)",
                            padding: 6,
                          }}
                        >
                          <img
                            src={unfoldIcon}
                            alt="Collapse video"
                            style={{ width: 20, height: 20 }}
                          />
                        </IconButton>
                      </Box>
                    ) : (
                      <>
                        {lastVideoRequest?.videoUrl && (
                          <Box
                            mb={2}
                            position="relative"
                            onMouseEnter={() => setIsVideoHovered(true)}
                            onMouseLeave={() => setIsVideoHovered(false)}
                            style={{
                              width: "100%",
                              minHeight: isMobile ? 270 : 400,
                              margin: "0 auto",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <video
                              ref={el => {
                                videoInPopupRef.current = el;
                              }}
                              onPlay={() => setIsVideoPlaying(true)}
                              onPause={() => setIsVideoPlaying(false)}
                              style={{
                                width: "100%",
                                borderRadius: "12px",
                                maxHeight: 320,
                                minHeight: isMobile ? 270 : 400,
                                objectFit: "cover",
                                cursor: "pointer",
                              }}
                              src={lastVideoRequest.videoUrl}
                            />
                            {/* Center play / pause button */}
                            {!isVideoPlaying ? (
                              <button
                                type="button"
                                onClick={e => {
                                  e.stopPropagation();
                                  videoInPopupRef.current?.play();
                                }}
                                style={{
                                  position: "absolute",
                                  left: "50%",
                                  top: "50%",
                                  transform: "translate(-50%, -50%)",
                                  width: 48,
                                  height: 48,
                                  borderRadius: "50%",
                                  backgroundColor: "#FFFFFF33",
                                  backdropFilter: "blur(3.38px)",
                                  WebkitBackdropFilter: "blur(3.38px)",
                                  border: "none",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: 0,
                                }}
                              >
                                <img
                                  src={playButtonIcon}
                                  alt="Play"
                                  style={{ width: 34, height: 34 }}
                                />
                              </button>
                            ) : isVideoHovered ? (
                              <button
                                type="button"
                                onClick={e => {
                                  e.stopPropagation();
                                  videoInPopupRef.current?.pause();
                                }}
                                style={{
                                  position: "absolute",
                                  left: "50%",
                                  top: "50%",
                                  transform: "translate(-50%, -50%)",
                                  width: 48,
                                  height: 48,
                                  borderRadius: "50%",
                                  backgroundColor: "#FFFFFF33",
                                  backdropFilter: "blur(3.38px)",
                                  WebkitBackdropFilter: "blur(3.38px)",
                                  border: "none",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: 0,
                                }}
                              >
                                <Pause size={24} color="#FFFFFF" />
                              </button>
                            ) : null}
                            {/* Bottom-right icon to expand video */}
                            <button
                              type="button"
                              onClick={e => {
                                e.stopPropagation();
                                setIsVideoExpanded(true);
                              }}
                              style={{
                                position: "absolute",
                                right: 16,
                                bottom: 16,
                                width: 36,
                                height: 36,
                                borderRadius: "50%",
                                backgroundColor: "#FFFFFF33",
                                backdropFilter: "blur(3.38px)",
                                WebkitBackdropFilter: "blur(3.38px)",
                                border: "none",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: 0,
                              }}
                            >
                              <img
                                src={vectorIcons}
                                alt="Expand"
                                style={{ width: 19, height: 19 }}
                              />
                            </button>
                          </Box>
                        )}
                        <Typography
                          style={{
                            fontSize: isMobile ? 16 : 20,
                            fontWeight: isMobile ? 700 : 600,
                            color: "#FFFFFF",
                            marginBottom: 8,
                          }}
                        >
                          Creator have delivered video on your request.
                        </Typography>
                        {lastVideoRequest?.creatorMessage && (
                          <>
                            <Typography
                              style={{
                                fontSize: 14,
                                fontWeight: 500,
                                color: "#FFFFFFCC",
                                marginTop: 12,
                                marginBottom: 4,
                              }}
                            >
                              Message from creator:
                            </Typography>
                            <Typography
                              style={{
                                fontSize: 14,
                                fontWeight: 400,
                                color: "#FFFFFF",
                                marginBottom: 16,
                              }}
                            >
                              {lastVideoRequest.creatorMessage}
                            </Typography>
                          </>
                        )}
                        <Typography
                          style={{
                            fontSize: 14,
                            fontWeight: 400,
                            color: isMobile ? "#FFFFFFCC" : "#FFFFFF",
                            marginBottom: 24,
                          }}
                        >
                          If you enjoyed a video you may compliment creator with
                          a tip or create a new request.
                        </Typography>

                        <Box
                          display="flex"
                          flexDirection="column"
                          mt={2}
                          width="100%"
                          style={{ gap: 16 }}
                        >
                          <Button
                            variant="contained"
                            className={classes.buttonAction}
                            fullWidth
                            style={{
                              width: "100%",
                              fontSize: isMobile ? 14 : 20,
                              backgroundColor: "#FE5A97",
                            }}
                            startIcon={
                              <img
                                src={sendVideoRequestIcon}
                                alt="Send video request"
                                style={{ width: 20, height: 20 }}
                              />
                            }
                            onClick={() => {
                              setVideoRequestInfoOpen(false);
                              setRequestVideoDialog(true);
                            }}
                          >
                            Send a video request
                          </Button>
                          <Button
                            variant="contained"
                            className={classes.buttonAction}
                            fullWidth
                            style={{
                              width: "100%",
                              fontSize: isMobile ? 14 : 20,
                            }}
                            startIcon={
                              <img
                                src={dollarIcon}
                                alt="Tip"
                                style={{ width: 20, height: 20 }}
                              />
                            }
                            onClick={() => {
                              setVideoRequestInfoOpen(false);
                              handleOpenTipDialog();
                            }}
                          >
                            Tip Creator
                          </Button>
                        </Box>
                      </>
                    )
                  ) : (
                    <>
                      <Typography
                        style={{
                          fontSize: isMobile ? 14 : 16,
                          fontWeight: 500,
                          color: isMobile ? "#FFFFFFCC" : "#FFFFFF",
                          marginBottom: 8,
                        }}
                      >
                        Creator have accepted your video request:
                      </Typography>
                      {lastVideoRequest?.description && (
                        <Box mt={1} mb={3}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={1}
                            variant="outlined"
                            value={lastVideoRequest.description}
                            InputProps={{
                              readOnly: true,
                              style: {
                                border: "none",
                                color: "#FFFFFF",
                                backgroundColor: "#1F1F1F80",
                                borderRadius: 8,
                              },
                            }}
                          />
                        </Box>
                      )}
                      <Typography
                        style={{
                          fontSize: isMobile ? 14 : 16,
                          fontWeight: 400,
                          color: "#FFFFFFCC",
                          marginBottom: 8,
                        }}
                      >
                        We will notify you as soon as your request has been
                        fulfilled.
                      </Typography>
                      <Typography
                        style={{
                          fontSize: isMobile ? 14 : 16,
                          fontWeight: 400,
                          color: "#A3A3A3",
                          marginBottom: 24,
                        }}
                      >
                        If you do not receive the video by March 12, your
                        payment will be automatically refunded.
                      </Typography>

                      <Box display="flex" justifyContent="center">
                        <Button
                          variant="contained"
                          className={classes.buttonAction}
                          onClick={async () => {
                            setVideoRequestInfoOpen(false);
                            await handleMessageClick();
                          }}
                          fullWidth
                          style={{
                            width: "100%",
                            fontSize: isMobile ? 14 : 20,
                            backgroundColor: "#FE5A97",
                          }}
                          startIcon={
                            <img
                              src={messageIcon}
                              alt="Message"
                              style={{ width: 20, height: 20 }}
                            />
                          }
                        >
                          Message creator
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </Modal>
          </>
        ) : (
          <>
            {/* Банер (за приватного профілю) */}
            {profileAccessMessage && (
              <Box
                style={{
                  color: "white",
                  textAlign: "center",
                  marginTop: 20,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "36px",
                }}
              >
                {lockedByPrivate && !isViewer ? (
                  <>
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: "16px",
                      }}
                    >
                      <img
                        src={lockIcon}
                        alt="Locked profile"
                        style={{
                          width: isMobile ? 40 : 60,
                          height: isMobile ? 40 : 60,
                        }}
                      />
                      <Typography
                        style={{
                          fontSize: isMobile ? 20 : 32,
                          fontWeight: 700,
                          color: "#FFFFFF",
                        }}
                      >
                        Subscribe to unlock profile
                      </Typography>
                    </Box>
                    <Button
                      className={classes.buttonAction}
                      onClick={() => {
                        openSubscribeDialogWithLoader();
                      }}
                      style={{
                        backgroundColor: "#FE5A97",
                        color: "#FFFFFF",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 4,
                        paddingTop: 8,
                        paddingBottom: 8,
                      }}
                    >
                      Subscribe
                    </Button>
                  </>
                ) : (
                  <Typography
                    style={{
                      fontSize: isMobile ? 20 : 32,
                      fontWeight: 700,
                      color: "#FFFFFF",
                    }}
                  >
                    {profileAccessMessage}
                  </Typography>
                )}
              </Box>
            )}
            {shouldShowPaidSubscriptionLock && !lockedByPrivate && (
              <Box
                style={{
                  color: "white",
                  textAlign: "center",
                  marginTop: 20,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "36px",
                }}
              >
                <Box
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  <img
                    src={lockIcon}
                    alt="Locked profile"
                    style={{
                      width: isMobile ? 40 : 60,
                      height: isMobile ? 40 : 60,
                    }}
                  />
                  <Typography
                    style={{
                      fontSize: isMobile ? 20 : 32,
                      fontWeight: 700,
                      color: "#FFFFFF",
                    }}
                  >
                    Subscribe to unlock profile
                  </Typography>
                </Box>
                <Button
                  className={classes.buttonAction}
                  onClick={() => {
                    openSubscribeDialogWithLoader();
                  }}
                  style={{
                    backgroundColor: "#FE5A97",
                    color: "#FFFFFF",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 4,
                    paddingTop: 8,
                    paddingBottom: 8,
                  }}
                >
                  Subscribe
                </Button>
              </Box>
            )}
            {!(lockedByPrivate && !isViewer) && !shouldShowPaidSubscriptionLock && (
              <>
                <Grid
                  container
                  className={classes.postsGrid}
                  style={{ position: "relative" }}
                >
                  {posts?.items
                    .filter(post =>
                      postsType === "all"
                        ? true
                        : post.content[0].type === postsType
                    )
                    .map(post => {
                      const isPremiumPost =
                        post.subscriptionType === "premium" && !isOwner;
                      const shouldShowUpgradeOverlay =
                        isPremiumPost &&
                        effectiveSubscriptionTier !== "premium";

                      return (
                        <Grid
                          item
                          xs={4}
                          sm={4}
                          md={4}
                          lg={4}
                          key={post._id}
                          className={classes.postItem}
                        >
                          <div
                            style={{ position: "relative" }}
                            onContextMenu={
                              !hasPremiumSubscription
                                ? (e: React.MouseEvent) => e.preventDefault()
                                : undefined
                            }
                          >
                            {/* Бейдж, якщо пост закріплено */}
                            {post.pinnedAt && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: 8,
                                  left: 8,
                                  zIndex: 3,
                                  background: "#FE5A97",
                                  color: "#fff",
                                  fontSize: 12,
                                  padding: "2px 6px",
                                  borderRadius: 6,
                                  pointerEvents: "none",
                                }}
                              >
                                Pinned
                              </div>
                            )}

                            {/* Кнопки Edit та Delete для власника поста */}
                            {isOwner && (
                              <>
                                <IconButton
                                  onClick={(
                                    e: React.MouseEvent<HTMLButtonElement>
                                  ) => {
                                    e.stopPropagation();
                                    handleEditPost(post);
                                  }}
                                  style={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    zIndex: 3,
                                    backgroundColor: "#FFFFFF4D",
                                    backdropFilter: "blur(2px)",
                                    WebkitBackdropFilter: "blur(2px)",
                                    color: "#fff",
                                    padding: 0,
                                    width: isMobile ? "20px" : "48px",
                                    height: isMobile ? "20px" : "48px",
                                  }}
                                >
                                  <img
                                    src={pencilIcon}
                                    alt="Edit"
                                    style={{
                                      width: isMobile ? 12 : 24,
                                      height: isMobile ? 12 : 24,
                                      objectFit: "contain",
                                    }}
                                  />
                                </IconButton>
                              </>
                            )}

                            {/*/!* Кнопка Pin/Unpin — лише власнику профілю *!/*/}
                            {/*{isOwner && (*/}
                            {/*    <Button*/}
                            {/*        size="small"*/}
                            {/*        onClick={(e) => {*/}
                            {/*          e.stopPropagation();          // щоб не відкривався діалог поста*/}
                            {/*          handleTogglePin(post);        // хендлер ми вже додали вище*/}
                            {/*        }}*/}
                            {/*        disabled={!!pinLoading[post._id]}*/}
                            {/*        style={{*/}
                            {/*          position: "absolute",*/}
                            {/*          top: 8,*/}
                            {/*          right: 8,*/}
                            {/*          zIndex: 3,*/}
                            {/*          minWidth: 64,*/}
                            {/*          padding: "2px 8px",*/}
                            {/*          background: "#333",*/}
                            {/*          color: "#fff",*/}
                            {/*          border: "1px solid #444",*/}
                            {/*          textTransform: "none",*/}
                            {/*        }}*/}
                            {/*    >*/}
                            {/*      {pinLoading[post._id] ? "..." : (post.pinnedAt ? "Unpin" : "Pin")}*/}
                            {/*    </Button>*/}
                            {/*)}*/}

                            <div
                              onClick={() => handlePostClick(post)}
                              style={{
                                cursor: canViewAllPosts
                                  ? "pointer"
                                  : "not-allowed",
                              }}
                            >
                              {(() => {
                                return (
                                  !canViewAllPosts &&
                                  post.content[0].type === "image"
                                );
                              })() && (
                                <>
                                  {loadingMedia[post.content[0].mediaUrl] ? (
                                    <div
                                      className={classes.postImg}
                                      style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        backgroundColor: "#1a1a1a",
                                      }}
                                    >
                                      <CircularProgress size={40} />
                                    </div>
                                  ) : mediaUrls[post.content[0].mediaUrl] ? (
                                    <img
                                      className={classes.postImg}
                                      src={mediaUrls[post.content[0].mediaUrl]}
                                      alt={`Post locked`}
                                      loading="lazy"
                                      onContextMenu={
                                        !hasPremiumSubscription
                                          ? (e: React.MouseEvent) =>
                                              e.preventDefault()
                                          : undefined
                                      }
                                      onDragStart={
                                        !hasPremiumSubscription
                                          ? (e: React.DragEvent) =>
                                              e.preventDefault()
                                          : undefined
                                      }
                                      draggable={
                                        !hasPremiumSubscription
                                          ? false
                                          : undefined
                                      }
                                    />
                                  ) : null}
                                </>
                              )}
                              {(() => {
                                return (
                                  canViewAllPosts &&
                                  post.content[0].type === "video"
                                );
                              })() && (
                                <>
                                  {loadingMedia[post.content[0].mediaUrl] ? (
                                    <div
                                      className={classes.postImg}
                                      style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        backgroundColor: "#1a1a1a",
                                      }}
                                    >
                                      <CircularProgress size={40} />
                                    </div>
                                  ) : !post.content[0].isBlurred &&
                                    canViewAllPosts &&
                                    mediaUrls[post.content[0].mediaUrl] ? (
                                    <video
                                      src={mediaUrls[post.content[0].mediaUrl]}
                                      autoPlay
                                      loop
                                      muted
                                      className={classes.postVideo}
                                      playsInline
                                      controls={false}
                                      preload="auto"
                                      onError={e => {
                                        const video =
                                          e.target as HTMLVideoElement;
                                        const src = video.src;
                                        const mediaUrl =
                                          post.content[0].mediaUrl;

                                        console.error("Video load error:", {
                                          src,
                                          mediaUrl,
                                          error: video.error,
                                          errorCode: video.error?.code,
                                          errorMessage: video.error?.message,
                                        });

                                        // Якщо blob URL не завантажився через URL safety check, не намагаємося перезавантажити
                                        if (
                                          video.error?.code === 4 ||
                                          video.error?.message?.includes(
                                            "URL safety check"
                                          )
                                        ) {
                                          console.error(
                                            "Blob URL rejected by browser security. Cannot retry.",
                                            { src, mediaUrl }
                                          );
                                          // Видаляємо blob URL з mediaUrls, щоб не показувати помилковий URL
                                          setMediaUrls(prev => {
                                            const next = { ...prev };
                                            delete next[mediaUrl];
                                            return next;
                                          });
                                          return;
                                        }

                                        // Якщо blob URL не завантажився, спробуємо перезавантажити (максимум MAX_RETRIES разів)
                                        if (
                                          src &&
                                          src.startsWith("blob:") &&
                                          mediaUrl
                                        ) {
                                          const currentRetries =
                                            retryCountRef.current.get(
                                              mediaUrl
                                            ) || 0;

                                          if (currentRetries >= MAX_RETRIES) {
                                            console.error(
                                              `Max retries reached for ${mediaUrl}. Stopping retry attempts.`
                                            );
                                            return;
                                          }

                                          // Збільшуємо лічильник спроб
                                          retryCountRef.current.set(
                                            mediaUrl,
                                            currentRetries + 1
                                          );

                                          // Видаляємо з fetchedUrls, щоб можна було спробувати знову
                                          fetchedUrlsRef.current.delete(
                                            mediaUrl
                                          );

                                          // Видаляємо blob URL з mediaUrls перед повторною спробою
                                          setMediaUrls(prev => {
                                            const next = { ...prev };
                                            delete next[mediaUrl];
                                            return next;
                                          });

                                          // Перезавантажуємо медіа, якщо воно ще не завантажується
                                          if (!loadingMedia[mediaUrl]) {
                                            fetchMediaUrl(
                                              mediaUrl,
                                              post.content[0].type
                                            );
                                          }
                                        }
                                      }}
                                      onLoadedData={() => {
                                        // console.log(
                                        //   "Video loaded successfully:",
                                        //   {
                                        //     mediaUrl: post.content[0].mediaUrl,
                                        //     src: mediaUrls[
                                        //       post.content[0].mediaUrl
                                        //     ],
                                        //   }
                                        // );
                                      }}
                                      onCanPlay={() => {
                                        const video = document.querySelector(
                                          `video[src*="${post.content[0].mediaUrl}"]`
                                        ) as HTMLVideoElement;
                                        if (video) {
                                          video.play().catch(err => {
                                            console.error(
                                              "Video play error:",
                                              err
                                            );
                                          });
                                        }
                                      }}
                                    />
                                  ) : (
                                    <img
                                      className={classes.postImg}
                                      src={
                                        mediaUrls[post.content[0].mediaUrl] ||
                                        post.content[0].mediaUrl ||
                                        videoIconSvg
                                      }
                                      alt={`Video preview ${post._id}`}
                                      loading="lazy"
                                      style={{
                                        // Для відео бекенд вже надає заблюрене прев'ю через mediaUrl, тому не застосовуємо CSS blur
                                        opacity:
                                          canViewAllPosts &&
                                          !post.content[0].isBlurred
                                            ? 1
                                            : 0.8,
                                      }}
                                    />
                                  )}
                                  {canViewAllPosts &&
                                    post.price > 0 &&
                                    !post.access &&
                                    !(me?.isAdmin || me?.isSuperAdmin) &&
                                    !isOwner && (
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: 0,
                                          left: 0,
                                          right: 0,
                                          bottom: 0,
                                          backgroundColor: "#00000099",
                                          zIndex: 3,
                                          pointerEvents: "none",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        {/* <Typography
                                        style={{
                                          color: "#FFFFFF",
                                          textAlign: "center",
                                          fontWeight: 700,
                                          fontSize: isMobile ? "14px" : "18px",
                                        }}
                                      >
                                        Purchase to unlock
                                      </Typography> */}
                                      </div>
                                    )}
                                  <img
                                    src={videoIconSvg}
                                    alt="Video Icon"
                                    width={20}
                                    height={20}
                                    style={{
                                      position: "absolute",
                                      top: "10px",
                                      right: "10px",
                                      fill: "white",
                                      zIndex: 4,
                                    }}
                                  />
                                </>
                              )}
                              {(() => {
                                return (
                                  !canViewAllPosts &&
                                  post.content[0].type === "video"
                                );
                              })() && (
                                <>
                                  {loadingMedia[post.content[0].mediaUrl] ? (
                                    <div
                                      className={classes.postImg}
                                      style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        backgroundColor: "#1a1a1a",
                                      }}
                                    >
                                      <CircularProgress size={40} />
                                    </div>
                                  ) : (
                                    <img
                                      className={classes.postImg}
                                      src={
                                        mediaUrls[post.content[0].mediaUrl] ||
                                        post.content[0].mediaUrl ||
                                        videoIconSvg
                                      }
                                      alt={`Video locked`}
                                      loading="lazy"
                                      style={{
                                        // Для відео бекенд вже надає заблюрене прев'ю через mediaUrl, тому не застосовуємо CSS blur
                                        opacity: 0.8,
                                      }}
                                    />
                                  )}
                                  <img
                                    src={videoIconSvg}
                                    alt="Video Icon"
                                    width={20}
                                    height={20}
                                    style={{
                                      position: "absolute",
                                      top: "10px",
                                      right: "10px",
                                      fill: "white",
                                      zIndex: 2,
                                    }}
                                  />
                                </>
                              )}
                              {(() => {
                                return (
                                  canViewAllPosts &&
                                  post.content[0].type === "image"
                                );
                              })() && (
                                <>
                                  {loadingMedia[post.content[0].mediaUrl] ? (
                                    <div
                                      className={classes.postImg}
                                      style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        backgroundColor: "#1a1a1a",
                                      }}
                                    >
                                      <CircularProgress size={40} />
                                    </div>
                                  ) : mediaUrls[post.content[0].mediaUrl] ? (
                                    <img
                                      className={classes.postImg}
                                      src={mediaUrls[post.content[0].mediaUrl]}
                                      alt={`Post ${post._id}`}
                                      loading="lazy"
                                      style={{
                                        filter:
                                          canViewAllPosts &&
                                          !post.content[0].isBlurred
                                            ? "none"
                                            : "blur(20px)",
                                        opacity:
                                          canViewAllPosts &&
                                          !post.content[0].isBlurred
                                            ? 1
                                            : 0.8,
                                      }}
                                    />
                                  ) : null}
                                  {canViewAllPosts &&
                                    post.price > 0 &&
                                    !post.access &&
                                    !(me?.isAdmin || me?.isSuperAdmin) &&
                                    !isOwner && (
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: 0,
                                          left: 0,
                                          right: 0,
                                          bottom: 0,
                                          backgroundColor: "#00000099",
                                          zIndex: 3,
                                          pointerEvents: "none",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        {/* <Typography
                                        style={{
                                          color: "#FFFFFF",
                                          textAlign: "center",
                                          fontWeight: 700,
                                          fontSize: isMobile ? "14px" : "18px",
                                        }}
                                      >
                                        Purchase to unlock
                                      </Typography> */}
                                      </div>
                                    )}
                                </>
                              )}
                              {post.price > 0 &&
                                !(me?.isAdmin || me?.isSuperAdmin) &&
                                !isOwner &&
                                !post.access && (
                                  <div
                                    className={classes.priceBlock}
                                    onClick={(
                                      e: React.MouseEvent<HTMLDivElement>
                                    ) => {
                                      e.stopPropagation();
                                      handlePurchasePost(post);
                                    }}
                                  >
                                    <Box
                                      className={classes.priceBlockIcon}
                                      component="span"
                                      aria-hidden
                                    >
                                      $
                                    </Box>

                                    <span>
                                      <span style={{ whiteSpace: "nowrap" }}>
                                        Purchase this{" "}
                                        {post.content[0].type === "video"
                                          ? "video"
                                          : "image"}
                                      </span>
                                      <br />
                                      <span className="price-text">
                                        for
                                      </span>{" "}
                                      <span className="price-amount">
                                        ${post.price}
                                      </span>
                                    </span>
                                  </div>
                                )}
                              {(post.access || isSubscribedOrTrial) &&
                                !post.content[0].isBlurred &&
                                post.commentsCount &&
                                post.commentsCount > 0 && (
                                  <Typography
                                    className={classes.commentsPreview}
                                    style={{
                                      position: "absolute",
                                      bottom: "10px",
                                      left: "10px",
                                      zIndex: 2,
                                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    View all {post.commentsCount} comments
                                  </Typography>
                                )}

                              {shouldShowUpgradeOverlay && (
                                <Box
                                  style={{
                                    position: "absolute",
                                    inset: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: 12,
                                    textAlign: "center",
                                    background:
                                      "linear-gradient(180deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.9) 100%)",
                                    zIndex: 4,
                                  }}
                                >
                                  <Box
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      gap: 8,
                                    }}
                                  >
                                    <img
                                      src={subscribeIcon}
                                      alt="Upgrade subscription"
                                      style={{ width: 32, height: 26 }}
                                    />
                                    <Typography
                                      style={{
                                        color: "#FFFFFF",
                                        fontSize: 14,
                                        fontWeight: 500,
                                      }}
                                    >
                                      Upgrade your Subscription
                                      <br />
                                      to unlock more exclusive content
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </div>
                          </div>
                        </Grid>
                      );
                    })}
                  {/* Overlay dimmer для непідписаних користувачів видалено за вимогою */}
                </Grid>
              </>
            )}
          </>
        )}
        {selectedPost && (
          <PostDialog
            open={dialogOpen}
            post={selectedPost}
            onClose={handleCloseDialog}
            onPinnedChange={(postId, pinnedAt) => {
              setPosts(prev => {
                if (!prev) return prev;
                const updated = prev.items.map(p =>
                  p._id === postId ? { ...p, pinnedAt } : p
                );
                return { ...prev, items: sortWithPins(updated) }; // твоя функція сортування
              });
            }}
            onPostDeleted={deletedPostId => {
              // Remove the deleted post from the current posts list
              setPosts(prev => {
                if (!prev) return prev;
                const updated = prev.items.filter(p => p._id !== deletedPostId);
                return { ...prev, items: updated };
              });

              // Clear any error messages by showing success feedback
              showToast("Post deleted successfully", "success");
            }}
            onEditPost={isOwner ? handleEditPost : undefined}
          />
        )}
        <Modal
          open={subscribeDialogVisible}
          onClose={() => {
            setSubscribeDialogVisible(false);
            setSubscribeStep(0);
            setSelectedTier("");
          }}
          disablePortal
          disableEnforceFocus
          className={classes.modalSubscriptionWrapper}
        >
          <Box
            className={classes.modalTipWrapper}
            style={{ display: subscribeDialogVisible ? "block" : "none" }}
          >
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px 24px",
                backgroundColor: "#131313",
                borderRadius: "16px",
              }}
            >
              {subscribeStep === 1 ? (
                <IconButton
                  onClick={() => {
                    setSubscribeStep(0);
                  }}
                  style={{
                    color: "#ffffff",
                    padding: "8px",
                  }}
                >
                  <ArrowLeft size={24} />
                </IconButton>
              ) : (
                <Box style={{ width: "40px" }} />
              )}
              <IconButton
                onClick={() => {
                  setSubscribeDialogVisible(false);
                  setSubscribeStep(0);
                  setSelectedTier("");
                }}
                style={{
                  color: "#ffffff",
                  padding: "8px",
                  marginLeft: "auto",
                }}
              >
                <X size={24} />
              </IconButton>
            </Box>
            <DialogContent className={classes.modalContent}>
              {subscribeModalLoading ? (
                <Box
                  style={{
                    width: "100%",
                    minHeight: 220,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  <CircularProgress size={28} style={{ color: "#FE5A97" }} />
                  <Typography style={{ color: "#FFFFFF", fontSize: 14 }}>
                    Checking subscription status...
                  </Typography>
                </Box>
              ) : (
                subscribeStep === 0 && (
                  <>
                    <Typography className={classes.modalSubtitle}>
                      {Array.isArray(displayCreatorPlans) &&
                      displayCreatorPlans.length > 1
                        ? "Select your subscription"
                        : "Subscribe and discover creators content"}
                    </Typography>
                    {Array.isArray(displayCreatorPlans) &&
                      displayCreatorPlans.length > 0 &&
                      displayCreatorPlans.some(
                        plan =>
                          plan.secondaryPrice != null && !!plan.secondaryPeriod
                      ) && (
                        <Box
                          style={{
                            display: "flex",
                            gap: 8,
                            marginTop: 16,
                            backgroundColor: "#1F1F1F",
                            borderRadius: 8,
                            padding: 4,
                            width: "fit-content",
                          }}
                        >
                          <Button
                            onClick={() => setBillingMode("monthly")}
                            style={{
                              minWidth: 0,
                              padding: isMobile ? "4px 10px" : "6px 16px",
                              width: 180,
                              borderRadius: 8,
                              backgroundColor:
                                billingMode === "monthly"
                                  ? "#FE5A97"
                                  : "transparent",
                              color: "#FFFFFF",
                              fontSize: isMobile ? 14 : 20,
                              fontWeight: 600,
                              textTransform: "none",
                            }}
                          >
                            Monthly -10%
                          </Button>
                          <Button
                            onClick={() => setBillingMode("weekly")}
                            style={{
                              minWidth: 0,
                              padding: isMobile ? "4px 10px" : "6px 16px",
                              width: 180,
                              borderRadius: 8,
                              backgroundColor:
                                billingMode === "weekly"
                                  ? "#FE5A97"
                                  : "transparent",
                              color: "#FFFFFF",
                              fontSize: isMobile ? 14 : 20,
                              fontWeight: 600,
                              textTransform: "none",
                            }}
                          >
                            Weekly
                          </Button>
                        </Box>
                      )}
                    <Box
                      className={classes.subscribePlansList}
                      style={{
                        flexDirection:
                          isMobile || !Array.isArray(displayCreatorPlans)
                            ? "column"
                            : displayCreatorPlans.length > 1
                              ? "row"
                              : "column",
                        overflowX:
                          !isMobile &&
                          Array.isArray(displayCreatorPlans) &&
                          displayCreatorPlans.length > 1
                            ? "auto"
                            : "visible",
                      }}
                    >
                      {displayCreatorPlans.map(plan => {
                        const subscribedToThisPlan = isPlanSubscribed(plan);
                        const hasBothPeriods =
                          plan?.secondaryPrice != null &&
                          !!plan?.secondaryPeriod;

                        // Визначаємо, яку періодичність та ціну показувати
                        const primaryIsMonth = plan.period === "month";
                        const secondaryIsMonth =
                          plan.secondaryPeriod === "month";
                        const wantsMonthly = billingMode === "monthly";

                        let resolvedPrice = plan.price;
                        let resolvedPeriod: "week" | "month" =
                          plan.period === "week" ? "week" : "month";

                        // Trial: у CCBill стягується $19.99 за перший тиждень,
                        // тому показуємо саме ціну, навіть якщо бекенд/дефолт
                        // тримає trial.price як 0.
                        if (plan.tier === "trial") {
                          resolvedPrice = trialRecurringPrice;
                          resolvedPeriod = "week";
                        }

                        if (hasBothPeriods) {
                          if (wantsMonthly) {
                            // шукаємо місячний варіант
                            if (primaryIsMonth) {
                              resolvedPrice = plan.price;
                              resolvedPeriod = "month";
                            } else if (secondaryIsMonth) {
                              resolvedPrice = plan.secondaryPrice ?? plan.price;
                              resolvedPeriod = "month";
                            }
                          } else {
                            // шукаємо тижневий варіант
                            if (plan.period === "week") {
                              resolvedPrice = plan.price;
                              resolvedPeriod = "week";
                            } else if (plan.secondaryPeriod === "week") {
                              resolvedPrice = plan.secondaryPrice ?? plan.price;
                              resolvedPeriod = "week";
                            }
                          }
                        }

                        return (
                          <Box
                            key={plan._id || `${plan.tier}-${plan.name}`}
                            style={{
                              background:
                                plan.tier === "trial"
                                  ? "transparent"
                                  : "radial-gradient(circle at top right, #ED9CB7 -156%, #191919 55%)",
                              backgroundColor:
                                plan.tier === "trial"
                                  ? "transparent"
                                  : "#191919",
                              borderRadius: "16px",
                              padding: "24px 16px 24px 16px",
                              border:
                                plan.tier === "trial"
                                  ? "1px solid #1F1F1F"
                                  : "1px solid #363636",
                              display: "flex",
                              flexDirection: "column",
                              gap: "16px",
                              width: "100%",
                              margin:
                                Array.isArray(displayCreatorPlans) &&
                                displayCreatorPlans.length > 1
                                  ? "0"
                                  : "0 auto",
                              maxWidth: "525px",
                            }}
                          >
                            <Box
                              style={{
                                display: "flex",
                                flexDirection:
                                  isMobile ||
                                  !Array.isArray(displayCreatorPlans)
                                    ? "column"
                                    : displayCreatorPlans.length > 1
                                      ? "column"
                                      : "row",
                                alignItems: "stretch",
                                gap: "16px",
                              }}
                            >
                              <Box
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  flex: 1,
                                }}
                              >
                                <Box
                                  style={{
                                    borderRadius: "20px",
                                    backgroundColor:
                                      plan.tier === "trial"
                                        ? "transparent"
                                        : "#FF78AA",
                                    border:
                                      plan.tier === "trial"
                                        ? "1px solid #FF78AA"
                                        : "1px solid transparent",
                                    color:
                                      plan.tier === "trial"
                                        ? "#FFFFFF"
                                        : "#0C0A09",
                                    fontSize: "16px",
                                    fontWeight: 500,
                                    padding: "6px 16px",
                                    marginBottom: "12px",
                                    width: "fit-content",
                                  }}
                                >
                                  {plan.name}
                                </Box>
                                <Box
                                  style={{
                                    display: "flex",
                                    alignItems: "baseline",
                                    gap: "8px",
                                  }}
                                >
                                  <Typography
                                    style={{
                                      fontSize: "32px",
                                      color: "#FFFFFF",
                                      fontWeight: 600,
                                    }}
                                  >
                                    ${resolvedPrice}
                                  </Typography>
                                  <Typography
                                    style={{
                                      fontSize: "18px",
                                      color: "#FFFFFF",
                                    }}
                                  >
                                    / {resolvedPeriod}
                                  </Typography>
                                </Box>
                                {plan?.tier === "trial" &&
                                plan?.trialAfterText ? (
                                  <Typography
                                    style={{
                                      fontSize: "18px",
                                      color: "#FFFFFF",
                                      opacity: 0.85,
                                      marginTop: "4px",
                                    }}
                                  >
                                    {plan.trialAfterText}
                                  </Typography>
                                ) : null}
                                {plan?.tier === "trial" &&
                                isTrialActive &&
                                trialEndsAtMs ? (
                                  <Typography
                                    style={{
                                      fontSize: "14px",
                                      color: "#FFFFFF",
                                      opacity: 0.85,
                                      marginTop: "8px",
                                    }}
                                  >
                                    Trial ends on{" "}
                                    {new Date(
                                      trialEndsAtMs
                                    ).toLocaleDateString()}
                                  </Typography>
                                ) : null}
                                <Typography
                                  style={{
                                    fontSize: "14px",
                                    color: "#FFFFFF",
                                    marginTop: "8px",
                                  }}
                                >
                                  Auto renewal, cancel anytime
                                </Typography>
                              </Box>
                              <Box
                                style={{
                                  width:
                                    Array.isArray(displayCreatorPlans) &&
                                    displayCreatorPlans.length > 1
                                      ? "100%"
                                      : "1px",
                                  height:
                                    Array.isArray(displayCreatorPlans) &&
                                    displayCreatorPlans.length > 1
                                      ? "1px"
                                      : "auto",
                                  alignSelf:
                                    Array.isArray(displayCreatorPlans) &&
                                    displayCreatorPlans.length > 1
                                      ? "center"
                                      : "stretch",
                                  background:
                                    Array.isArray(displayCreatorPlans) &&
                                    displayCreatorPlans.length > 1
                                      ? "linear-gradient(to right, #191919 0%, #D7A7B91A 100%)"
                                      : "linear-gradient(to bottom, #191919 0%, #D7A7B91A 100%)",
                                }}
                              />
                              <Box
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  flex: 1,
                                  gap: "6px",
                                }}
                              >
                                {Array.isArray(plan.memberBenefits) &&
                                  plan.memberBenefits.map((b: string) => (
                                    <Box
                                      key={b}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                      }}
                                    >
                                      <img src={markIcon} alt="" />
                                      <Typography
                                        style={{
                                          fontSize: "14px",
                                          color: "#FFFFFF",
                                        }}
                                      >
                                        {b}
                                      </Typography>
                                    </Box>
                                  ))}
                              </Box>
                            </Box>

                            <Box
                              style={{
                                marginTop:
                                  Array.isArray(displayCreatorPlans) &&
                                  displayCreatorPlans.length > 1
                                    ? "auto"
                                    : "16px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "12px",
                              }}
                            >
                              {(() => {
                                const tierLc = String(
                                  plan?.tier || ""
                                ).toLowerCase();
                                const isFreePlan =
                                  Boolean((plan as any)?.isFree) ||
                                  tierLc === "free";
                                const tp = plan?.trialPeriod;
                                const planTrialOn =
                                  tp === "week" || tp === "month";
                                // Показ лише за plan.trialPeriod (week/month). meta.trialEnabled / check.isTrialAvailable
                                // часто не приходять з API — не ховаємо CTA через них; помилку дає startTrial.
                                const showTrialCta =
                                  !isFreePlan &&
                                  tierLc !== "trial" &&
                                  !subscribedToThisPlan &&
                                  !isAlreadySubscribed &&
                                  !isTrialActive &&
                                  planTrialOn &&
                                  isTrialAvailable === true &&
                                  trialUsed !== true &&
                                  String(currentSubscriptionTier || "").toLowerCase() !==
                                    "trial";
                                // #region agent log
                                fetch("http://127.0.0.1:7574/ingest/4c4a54fc-6a32-45fd-bdf3-4c3c66ca0dc3", { method: "POST", headers: { "Content-Type": "application/json", "X-Debug-Session-Id": "01f43b" }, body: JSON.stringify({ sessionId: "01f43b", runId: "pre-fix-trial", hypothesisId: "T3", location: "profile-viewer.tsx:5225", message: "trial CTA visibility evaluated", data: { creatorId, planId: String(plan?._id || ""), tier: tierLc, planTrialOn, isAlreadySubscribed, isTrialActive, isTrialAvailable, trialUsed, currentSubscriptionTier, subscribedToThisPlan, showTrialCta }, timestamp: Date.now() }) }).catch(() => {});
                                // #endregion

                                if (!showTrialCta) return null;

                                return (
                                  <Button
                                    variant="outlined"
                                    fullWidth
                                    style={{
                                      border: "1px solid #FE5A97",
                                      color: "#FFFFFF",
                                      backgroundColor: "transparent",
                                      textTransform: "none",
                                      fontSize: 16,
                                      fontWeight: 600,
                                      height: "48px",
                                      borderRadius: 8,
                                    }}
                                    onClick={() =>
                                      (() => {
                                        // #region agent log
                                        fetch("http://127.0.0.1:7574/ingest/4c4a54fc-6a32-45fd-bdf3-4c3c66ca0dc3", { method: "POST", headers: { "Content-Type": "application/json", "X-Debug-Session-Id": "01f43b" }, body: JSON.stringify({ sessionId: "01f43b", runId: "pre-fix-trial-flexform", hypothesisId: "F1", location: "profile-viewer.tsx:5275", message: "trial CTA switched to flexform checkout step", data: { creatorId, planId: String(plan?._id || ""), planName: String(plan?.name || ""), trialPeriod: String(plan?.trialPeriod || ""), isTrialAvailable, trialUsed }, timestamp: Date.now() }) }).catch(() => {});
                                        // #endregion
                                        setTrialCheckoutPlanId(String(plan._id));
                                        setSelectedTier(plan.name);
                                        setSubscribeStep(1);
                                      })()
                                    }
                                  >
                                    {tp === "month"
                                      ? "1 Month Trial"
                                      : "1 Week Trial"}
                                  </Button>
                                );
                              })()}

                              <Button
                                variant={
                                  plan.tier === "trial"
                                    ? "outlined"
                                    : "contained"
                                }
                                color="primary"
                                fullWidth
                                style={{
                                  backgroundColor:
                                    plan.tier === "trial"
                                      ? "transparent"
                                      : "#FE5A97",
                                  border:
                                    plan.tier === "trial"
                                      ? "1px solid #FE5A97"
                                      : "1px solid transparent",
                                  color: "#FFFFFF",
                                  textTransform: "capitalize",
                                  fontSize: 16,
                                  fontWeight: 600,
                                  height: "48px",
                                  borderRadius: 8,
                                }}
                                disabled={
                                  subscribedToThisPlan ||
                                  (String(plan?.tier || "").toLowerCase() ===
                                    "trial" &&
                                    (isAlreadySubscribed ||
                                      isTrialActive ||
                                      !isTrialAvailable)) ||
                                  ((plan as any)?.isFree && isAlreadySubscribed)
                                }
                                onClick={() => {
                                  if (subscribedToThisPlan) return;
                                  const tier = String(
                                    plan?.tier || ""
                                  ).toLowerCase();
                                  const isFreePlan =
                                    Boolean((plan as any)?.isFree) ||
                                    tier === "free";
                                  if (isFreePlan) {
                                    if (!freeEnabled) {
                                      showToast(
                                        "Free subscription is disabled",
                                        "info"
                                      );
                                      return;
                                    }
                                    if (isAlreadySubscribed) {
                                      showToast(
                                        "You already have an active subscription",
                                        "info"
                                      );
                                      return;
                                    }
                                    handleSubscribeFree();
                                    return;
                                  }
                                  if (tier === "trial") {
                                    return;
                                  }
                                  setTrialCheckoutPlanId(null);
                                  setSelectedTier(plan.name);
                                  setSubscribeStep(1);
                                }}
                              >
                                {subscribedToThisPlan
                                  ? "Subscribed"
                                  : (plan as any)?.isFree
                                    ? "Subscribe Free"
                                    : "Subscribe"}
                              </Button>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </>
                )
              )}
              {subscribeStep === 1 &&
                selectedTier &&
                (() => {
                  const selectedPlan = displayCreatorPlans.find(
                    plan => plan.name === selectedTier
                  );

                  let planPrice = 0;
                  let planPeriod: "week" | "month" = "month";

                  if (selectedPlan) {
                    const hasBothPeriods =
                      selectedPlan.secondaryPrice != null &&
                      !!selectedPlan.secondaryPeriod;
                    const primaryIsMonth = selectedPlan.period === "month";
                    const secondaryIsMonth =
                      selectedPlan.secondaryPeriod === "month";
                    const wantsMonthly = billingMode === "monthly";

                    planPrice = selectedPlan.price;
                    planPeriod =
                      selectedPlan.period === "week" ? "week" : "month";

                    if (hasBothPeriods) {
                      if (wantsMonthly) {
                        if (primaryIsMonth) {
                          planPrice = selectedPlan.price;
                          planPeriod = "month";
                        } else if (secondaryIsMonth) {
                          planPrice =
                            selectedPlan.secondaryPrice ?? selectedPlan.price;
                          planPeriod = "month";
                        }
                      } else {
                        if (selectedPlan.period === "week") {
                          planPrice = selectedPlan.price;
                          planPeriod = "week";
                        } else if (selectedPlan.secondaryPeriod === "week") {
                          planPrice =
                            selectedPlan.secondaryPrice ?? selectedPlan.price;
                          planPeriod = "week";
                        }
                      }
                    }
                  }

                  const selectedTierLc = String(selectedPlan?.tier || "").toLowerCase();
                  const trialPeriodRaw = String(selectedPlan?.trialPeriod || "").toLowerCase();
                  const trialDays = trialPeriodRaw === "month" ? 30 : 7;
                  const isTrialCheckout =
                    !!selectedPlan &&
                    (selectedTierLc === "trial" ||
                      trialCheckoutPlanId === String(selectedPlan?._id));

                  return (
                    <CCBillPayment
                      price={
                        isTrialCheckout ? 0 : planPrice
                      }
                      context="subscription"
                      title="Secure payment"
                      buttonText={
                        isTrialCheckout
                          ? "Start Free Trial with CCBill"
                          : `Subscribe to ${selectedTier}`
                      }
                      description={
                        isTrialCheckout
                          ? `Start your free ${trialDays}-day trial now. Then continue for $${planPrice} per ${planPeriod}.`
                          : `Complete your ${selectedTier} subscription for $${planPrice} per ${planPeriod}`
                      }
                      subscriptionInitialPrice={
                        isTrialCheckout ? 0 : undefined
                      }
                      subscriptionInitialPeriod={
                        isTrialCheckout ? trialDays : undefined
                      }
                      subscriptionRecurringPrice={
                        isTrialCheckout ? planPrice : undefined
                      }
                      subscriptionRecurringPeriod={
                        isTrialCheckout
                          ? planPeriod === "week"
                            ? 7
                            : 30
                          : planPeriod === "week"
                            ? 7
                            : 30
                      }
                      onProceed={handleSubscribe}
                      onPaymentSuccess={() => {
                        if (selectedPlan) {
                          if (isTrialCheckout) {
                            // #region agent log
                            fetch("http://127.0.0.1:7574/ingest/4c4a54fc-6a32-45fd-bdf3-4c3c66ca0dc3", { method: "POST", headers: { "Content-Type": "application/json", "X-Debug-Session-Id": "01f43b" }, body: JSON.stringify({ sessionId: "01f43b", runId: "pre-fix-trial-flexform", hypothesisId: "F2", location: "profile-viewer.tsx:5455", message: "trial checkout payment success callback", data: { creatorId, planId: String(selectedPlan?._id || ""), trialDays, planPrice, planPeriod }, timestamp: Date.now() }) }).catch(() => {});
                            // #endregion
                            const endsAtMs =
                              Date.now() + trialDays * 24 * 60 * 60 * 1000;
                            setTrialEndsAtMs(endsAtMs);
                            persistTrial(endsAtMs);
                          }
                          showToast(
                            isTrialCheckout
                              ? `Trial activated for ${trialDays} days`
                              : `Successfully subscribed to ${selectedTier} for $${planPrice}!`,
                            "success"
                          );
                          setSubscribeDialogVisible(false);
                          setSelectedTier("");
                          setSubscribeStep(0);
                          setTrialCheckoutPlanId(null);
                        }
                      }}
                      userId={me?.id}
                      subscriptionToUserId={profile?._id}
                      email={me?.email}
                      isAlreadySubscribedToPlan={
                        selectedPlan ? isPlanSubscribed(selectedPlan) : false
                      }
                      autoProceed={isTrialCheckout}
                      onRedirectToCCBill={() => {
                        if (!isTrialCheckout) return;
                        // #region agent log
                        fetch("http://127.0.0.1:7574/ingest/4c4a54fc-6a32-45fd-bdf3-4c3c66ca0dc3", { method: "POST", headers: { "Content-Type": "application/json", "X-Debug-Session-Id": "01f43b" }, body: JSON.stringify({ sessionId: "01f43b", runId: "pre-fix-trial-flexform", hypothesisId: "F4", location: "profile-viewer.tsx:5468", message: "closing subscribe popup before ccbill redirect for trial", data: { creatorId, selectedTier, isTrialCheckout, planId: String(selectedPlan?._id || "") }, timestamp: Date.now() }) }).catch(() => {});
                        // #endregion
                        setSubscribeDialogVisible(false);
                        setSelectedTier("");
                        setSubscribeStep(0);
                        setTrialCheckoutPlanId(null);
                      }}
                      subscriptionSummary={
                        selectedPlan
                          ? {
                              planName: selectedPlan.name,
                              benefits: Array.isArray(
                                selectedPlan.memberBenefits
                              )
                                ? selectedPlan.memberBenefits
                                : [],
                              billingPeriod:
                                selectedPlan.tier === "trial"
                                  ? "week"
                                  : planPeriod,
                            }
                          : undefined
                      }
                    />
                  );
                })()}
            </DialogContent>
          </Box>
        </Modal>
        <TipDialog
          open={tipDialogVisible}
          onClose={() => setTipDialogVisible(false)}
          suggestedTips={profile?.suggestedTips}
          minimumTipAmount={profile?.minimumTipAmount}
          username={profile?.username}
          creatorName={
            profile ? profileDisplayName : undefined
          }
          tipForUserId={profile?._id}
          userId={me?.id}
          email={me?.email}
          onPaymentSuccess={handleTipSuccess}
          validateBeforeStep1={amount => {
            if (!profile?._id || !me?.id || amount <= 0) {
              showToast("Please enter a valid tip amount.", "error");
              return false;
            }
            return true;
          }}
        />
        {selectedPostForPurchase && purchasePostDialogVisible && (
          <UnlockPaidContentModal
            open={purchasePostDialogVisible}
            onClose={() => {
              setPurchasePostDialogVisible(false);
              setSelectedPostForPurchase(null);
            }}
            previewUrl={
              mediaUrls[selectedPostForPurchase.content[0]?.mediaUrl] ||
              videoIconSvg
            }
            isVideo={selectedPostForPurchase.content[0]?.type === "video"}
            price={selectedPostForPurchase.price}
            userId={me?.id || (me as any)?._id || ""}
            email={me?.email || ""}
            postId={selectedPostForPurchase._id}
            tipForUserId={selectedPostForPurchase.userId}
            onProceed={handleCompletePurchase}
            onPaymentSuccess={handlePurchaseSuccess}
          />
        )}
        <Modal
          open={cancelSubscriptionDialogVisible}
          onClose={() => setCancelSubscriptionDialogVisible(false)}
          disablePortal
          disableEnforceFocus
          className={classes.modalSubscriptionWrapper}
        >
          <Box
            className={classes.modalTipWrapper}
            style={{
              display: cancelSubscriptionDialogVisible ? "block" : "none",
            }}
          >
            <DialogContent className={classes.modalContent}>
              <Box
                style={{
                  position: "relative",
                  display: "flex",
                  width: "100%",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 16,
                  paddingTop: 24,
                }}
              >
                <IconButton
                  aria-label="Close"
                  onClick={() => setCancelSubscriptionDialogVisible(false)}
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    color: "#FFFFFF",
                  }}
                >
                  <X size={20} />
                </IconButton>
                <img
                  src={cancelSubscriptionImage}
                  alt="Cancel subscription"
                  style={{
                    width: isMobile ? 120 : 200,
                    height: isMobile ? 120 : 200,
                  }}
                />
                <Typography
                  style={{
                    fontSize: isMobile ? 20 : 32,
                    fontWeight: 700,
                    color: "#FFFFFF",
                    textAlign: "center",
                    maxWidth: 580,
                  }}
                >
                  Are you sure you want to cancel your subscription?
                </Typography>
                <Typography
                  style={{
                    fontSize: isMobile ? 16 : 20,
                    fontWeight: 400,
                    color: "#FFFFFFCC",
                    textAlign: "center",
                    marginTop: 4,
                  }}
                >
                  Exclusive content will be unavailabe to you from the moment of
                  cancelation
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions className={classes.modalActions}>
              <Button
                onClick={handleCancelSubscription}
                className={classes.sendButton}
                disabled={cancellingSubscription}
              >
                {cancellingSubscription ? (
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <CircularProgress size={20} style={{ color: "#fff" }} />
                    Cancelling...
                  </Box>
                ) : (
                  "Cancel subscription"
                )}
              </Button>
              {hasUpgradeSubscriptionOptions && (
                <Button
                  onClick={() => {
                    setCancelSubscriptionDialogVisible(false);
                    openSubscribeDialogWithLoader();
                  }}
                  className={classes.cancelButton}
                >
                  See subscription options
                </Button>
              )}
            </DialogActions>
          </Box>
        </Modal>
        <Menu
          anchorEl={subscriptionMenuEl}
          open={Boolean(subscriptionMenuEl)}
          onClose={closeSubscriptionMenu}
          keepMounted
          className={classes.subscribeDropdownMenu}
        >
          {isAlreadySubscribed && hasUpgradeSubscriptionOptions && (
            <MenuItem
              style={{
                borderRadius: 6,
                backgroundColor: "#FFFFFF08",
                padding: 0,
                marginBottom: 4,
              }}
              onClick={() => {
                closeSubscriptionMenu();
                if (hasUpgradeSubscriptionOptions) {
                  openSubscribeDialogWithLoader();
                } else {
                  showToast("No upgrade subscription options available", "info");
                }
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                style={{ gap: 8, padding: 12 }}
              >
                <img
                  src={subscribeIcon}
                  alt="Upgrade"
                  style={{ width: 24, height: 24 }}
                />
                <Typography
                  style={{ fontSize: 14, fontWeight: 500, color: "#FFFFFF" }}
                >
                  Upgrade subscription
                </Typography>
              </Box>
            </MenuItem>
          )}
          <MenuItem
            style={{
              borderRadius: 6,
              backgroundColor: "#FFFFFF08",
              padding: 0,
            }}
            onClick={async () => {
              closeSubscriptionMenu();
              try {
                const res: any = await checkUserSubscriptionAPI(creatorId);
                const isActive = Boolean(res?.isActive ?? res?.isSubscribed);
                setIsAlreadySubscribed(isActive);
                setSubscriptionEndDate(
                  typeof res?.endDate === "string" ? res.endDate : null
                );
                setCurrentSubscriptionTier(
                  typeof res?.tier === "string" ? res.tier.toLowerCase() : null
                );
                setIsTrialAvailable(Boolean(res?.isTrialAvailable));
                setTrialUsed(Boolean(res?.trialUsed));
                setCancelSubscriptionDialogVisible(true);
              } catch (error) {
                console.error("Error fetching subscription status:", error);
                showToast("Failed to load subscription status", "error");
              }
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              style={{ gap: 8, padding: 12 }}
            >
              <IconButton
                size="small"
                style={{
                  color: "#FFFFFF",
                  padding: 4,
                }}
              >
                <X size={16} />
              </IconButton>
              <Typography
                style={{ fontSize: 14, fontWeight: 500, color: "#FFFFFF" }}
              >
                Unsubscribe
              </Typography>
            </Box>
          </MenuItem>
        </Menu>
        {pendingCancelModalVisible && (
          <Modal
            open={pendingCancelModalVisible}
            onClose={() => setPendingCancelModalVisible(false)}
            className={classes.modalSubscriptionWrapper}
          >
            <Box className={classes.modalTipWrapper}>
              <DialogTitle className={classes.modalTitle}>
                Subscription Active
              </DialogTitle>
              <DialogContent className={classes.modalContent}>
                {currentSubscriptionTier === "free" ? (
                  <Typography
                    style={{
                      marginTop: 10,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    Your current subscription is <strong>Free</strong>.
                    <strong>No expiration date.</strong>
                  </Typography>
                ) : subscriptionEndDate ? (
                  <Typography
                    style={{
                      marginTop: 10,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    Your subscription has been canceled and will end on:{" "}
                    <strong>
                      {new Date(subscriptionEndDate).toLocaleDateString()}
                    </strong>
                  </Typography>
                ) : null}
              </DialogContent>
              <DialogActions className={classes.modalActions}>
                <Button
                  onClick={() => setPendingCancelModalVisible(false)}
                  className={classes.cancelButton}
                >
                  Close
                </Button>
              </DialogActions>
            </Box>
          </Modal>
        )}
        <Menu
          anchorEl={shareMenuEl}
          open={Boolean(shareMenuEl)}
          onClose={closeShareMenu}
          keepMounted
        >
          <MenuItem
            onClick={async () => {
              await handleLinkClick("profile");
              await copyToClipboard(profileURL);
              closeShareMenu();
            }}
          >
            Copy link
          </MenuItem>
          <MenuItem onClick={shareToTelegram}>Share to Telegram</MenuItem>
          <MenuItem onClick={shareToWhatsApp}>Share to WhatsApp</MenuItem>
          <MenuItem onClick={shareToFacebook}>Share to Facebook</MenuItem>
          <MenuItem onClick={shareToX}>Share to X</MenuItem>
          <MenuItem onClick={shareByEmail}>Share via Email</MenuItem>
        </Menu>
        {/* Share profile — той самий попап що при Share post на головній */}
        <ShareLinkDialog
          open={sharePopupOpen}
          onClose={() => {
            setSharePopupOpen(false);
            setProfileShareData(null);
          }}
          shareData={profileShareData}
        />
        {/* Edit Post Modal */}
        <Modal
          open={editPostDialogVisible}
          onClose={() => {
            setEditPostDialogVisible(false);
            setSelectedPostForEdit(null);
            setRawHashtagInput("");
            setEditSubscriptionType("basic");
            restoreBodyScrollAfterEditPost();
          }}
          className={classes.modalSubscriptionWrapper}
          disableScrollLock
        >
          <Box className={classes.editPostModalWrapper}>
            <DialogContent className={classes.editPostModalContent}>
              <Box className={classes.editPostLayout}>
                <Box className={classes.editPostMediaPane}>
                  {selectedPostForEdit?.content?.[0]?.type === "video" ? (
                    <video
                      src={selectedPostForEdit.content[0]?.mediaUrl}
                      className={classes.editPostMediaVideo}
                      controls
                      playsInline
                    />
                  ) : (
                    <img
                      src={selectedPostForEdit?.content?.[0]?.mediaUrl}
                      alt="Post preview"
                      className={classes.editPostMediaImage}
                    />
                  )}
                </Box>

                <Box className={classes.editPostFormPane}>
                  <Box className={classes.editPostCreatorHeader}>
                    <Avatar
                      src={displayAvatarSrc}
                      className={classes.editPostCreatorAvatar}
                    />
                    <Box>
                      <Typography className={classes.editPostCreatorName}>
                        {profileDisplayName}
                      </Typography>
                      <Typography className={classes.editPostCreatorUsername}>
                        @{profile?.username}
                      </Typography>
                    </Box>
                  </Box>

                  <Box style={{ marginTop: "24px", marginBottom: "16px", width: "100%" }}>
                    <Typography
                      style={{ color: "#fff", marginBottom: 8, fontWeight: 500 }}
                    >
                      Subscription types
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      value={editSubscriptionType}
                      onChange={e =>
                        setEditSubscriptionType(
                          e.target.value as "basic" | "premium"
                        )
                      }
                      variant="outlined"
                      className={classes.editPostTextField}
                      SelectProps={{
                        native: false,
                      }}
                    >
                      <MenuItem value="basic">Basic</MenuItem>
                      <MenuItem value="premium">Premium</MenuItem>
                    </TextField>
                  </Box>

                  <Box style={{ marginTop: "8px", marginBottom: "16px", width: "100%" }}>
                    <Typography
                      style={{ color: "#fff", marginBottom: 8, fontWeight: 500 }}
                    >
                      Caption
                    </Typography>
                    <TextField
                      multiline
                      rows={4}
                      fullWidth
                      value={editDescription}
                      onChange={e => setEditDescription(e.target.value)}
                      variant="outlined"
                      inputProps={{ maxLength: 2000 }}
                      placeholder="Tell users about your media..."
                      helperText={`${editDescription.length}/2000 characters`}
                      FormHelperTextProps={{
                        style: {
                          color: "#747474",
                          fontSize: isMobile ? "12px" : "14px",
                          fontWeight: 400,
                          marginBottom: 0,
                          marginLeft: 0,
                        },
                      }}
                      className={classes.editPostTextField}
                    />
                  </Box>

                  <Box style={{ marginTop: "8px", marginBottom: "16px", width: "100%" }}>
                    <Typography
                      style={{ color: "#fff", marginBottom: 8, fontWeight: 500 }}
                    >
                      Hashtags
                    </Typography>
                    <Box
                      style={{
                        width: "100%",
                        minHeight: 56,
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1px solid #2E2E2E",
                        background:
                          editHashtags.length >= 5 ? "#2a2a2a" : "#121212",
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "10px",
                        opacity: editHashtags.length >= 5 ? 0.6 : 1,
                      }}
                    >
                      {editHashtags.map((tag, index) => (
                        <Box
                          key={index}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            color: "#fff",
                            fontSize: isMobile ? "14px" : "20px",
                            fontWeight: 400,
                            lineHeight: 1,
                            whiteSpace: "nowrap",
                            padding: "4px 12px",
                            border: "1px solid #FFFFFF0D",
                            borderRadius: "6px",
                            background: "#FFFFFF0D",
                          }}
                        >
                          <span>{`#${tag}`}</span>
                          <Button
                            onClick={() => removeHashtag(tag)}
                            style={{
                              minWidth: "18px",
                              width: "18px",
                              height: "18px",
                              marginLeft: "6px",
                              color: "#FFD0E1",
                              fontSize: "16px",
                              padding: 0,
                              lineHeight: "18px",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              alignSelf: "center",
                            }}
                            classes={{ label: classes.categoryRemoveLabel }}
                          >
                            ×
                          </Button>
                        </Box>
                      ))}
                      <input
                        type="text"
                        value={rawHashtagInput}
                        placeholder={
                          editHashtags.length === 0
                            ? "#sugarcribs #luxury #hot #new #exclusive"
                            : ""
                        }
                        onChange={handleHashtagInputChange}
                        onKeyPress={handleHashtagKeyPress}
                        onKeyDown={handleHashtagInputKeyDown}
                        disabled={editHashtags.length >= 5}
                        style={{
                          flex: 1,
                          minWidth: 160,
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          color: editHashtags.length >= 5 ? "#666" : "#fff",
                          fontSize: "14px",
                          lineHeight: 1,
                          padding: 0,
                        }}
                      />
                    </Box>
                    <Typography
                      style={{
                        color: "#FFFFFF",
                        fontSize: isMobile ? "12px" : "14px",
                        fontWeight: 400,
                        marginTop: "4px",
                      }}
                    >
                      {editHashtags.length}/5
                    </Typography>
                  </Box>

                  <Box style={{ marginTop: "8px", marginBottom: "16px", width: "100%" }}>
                    <Typography
                      style={{ color: "#fff", marginBottom: 8, fontWeight: 500 }}
                    >
                      Categories
                    </Typography>
                    <Box
                      style={{
                        width: "100%",
                        minHeight: 56,
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1px solid #2E2E2E",
                        background:
                          editCategories.length >= 5 ? "#2a2a2a" : "#121212",
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "10px",
                        opacity: editCategories.length >= 5 ? 0.6 : 1,
                      }}
                    >
                      {editCategories.map((category, index) => {
                        const categoryName = categoryNamesById[category] || category;
                        return (
                          <Box
                            key={index}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              color: "#fff",
                              fontSize: isMobile ? "14px" : "20px",
                              fontWeight: 400,
                              lineHeight: 1,
                              whiteSpace: "nowrap",
                              padding: "4px 12px",
                              border: "1px solid #FFFFFF0D",
                              borderRadius: "6px",
                              background: "#FFFFFF0D",
                            }}
                          >
                            {categoryName}
                            <Button
                              onClick={() => removeCategory(category)}
                              style={{
                                minWidth: "18px",
                                width: "18px",
                                height: "18px",
                                marginLeft: "6px",
                                color: "#FFD0E1",
                                fontSize: "16px",
                                padding: 0,
                                lineHeight: 1,
                              }}
                              classes={{ label: classes.categoryRemoveLabel }}
                            >
                              ×
                            </Button>
                          </Box>
                        );
                      })}
                      <TextField
                        select
                        value=""
                        onChange={handleCategorySelectChange}
                        disabled={editCategories.length >= 5}
                        variant="outlined"
                        SelectProps={{ native: false, displayEmpty: true }}
                        style={{
                          flex: 1,
                          minWidth: 160,
                          margin: 0,
                        }}
                        className={`${classes.editPostTextField} ${classes.editPostCategorySelect}`}
                      >
                        <MenuItem value="" disabled>
                          {editCategories.length >= 5
                            ? "Maximum 5 categories reached"
                            : "Select category"}
                        </MenuItem>
                        {availableCategories.map(category => (
                          <MenuItem
                            key={category._id}
                            value={category._id}
                            disabled={editCategories.includes(category._id)}
                          >
                            {category.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    <Typography
                      style={{
                        color: "#FFFFFF",
                        fontSize: isMobile ? "12px" : "14px",
                        fontWeight: 400,
                        marginTop: "4px",
                      }}
                    >
                      {editCategories.length}/5
                    </Typography>
                  </Box>

                  {isPaidPostType(selectedPostForEdit?.type) && (
                    <TextField
                      label="Price"
                      type="text"
                      fullWidth
                      value={editPriceInput}
                      onChange={e => {
                        const parsedValue = parsePostPriceInput(e.target.value);
                        setEditPrice(parsedValue);
                        setEditPriceInput(formatPostPriceInput(parsedValue));
                      }}
                      variant="outlined"
                      inputProps={{
                        inputMode: "numeric",
                        "aria-label": "Price in dollars and cents",
                      }}
                      placeholder="$0.00"
                      helperText="Minimum price: $5.00 (for private posts)"
                      className={classes.editPostTextField}
                    />
                  )}

                  {selectedPostForEdit && (
                    <button
                      type="button"
                      className={classes.editPostDeleteText}
                      onClick={() => handleRequestDeletePost(selectedPostForEdit)}
                    >
                      Delete Post
                    </button>
                  )}

                  <Box className={classes.editPostActions}>
                    <Button
                      onClick={() => {
                        setEditPostDialogVisible(false);
                        setSelectedPostForEdit(null);
                        setRawHashtagInput("");
                        setEditSubscriptionType("basic");
                      }}
                      className={classes.editPostCancelButton}
                      disabled={isSavingPost}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSavePostEdit}
                      className={classes.editPostSaveButton}
                      disabled={isSavingPost}
                    >
                      {isSavingPost ? (
                        <>
                          <CircularProgress size={16} style={{ marginRight: 8 }} />
                          Saving...
                        </>
                      ) : (
                        "Save Changes"
                      )}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </DialogContent>
          </Box>
        </Modal>

        <Modal
          open={deletePostConfirmOpen}
          onClose={() => {
            if (deletingPost) return;
            setDeletePostConfirmOpen(false);
            setPostPendingDelete(null);
          }}
          className={classes.modalSubscriptionWrapper}
          disableScrollLock
        >
          <Box className={classes.deletePostModalWrapper}>
            <Box className={classes.deletePostHeader}>
              <Typography className={classes.deletePostTitle}>
                Delete post
              </Typography>
              <IconButton
                className={classes.deletePostClose}
                onClick={() => {
                  if (deletingPost) return;
                  setDeletePostConfirmOpen(false);
                  setPostPendingDelete(null);
                }}
                aria-label="Close delete confirmation"
              >
                <X size={20} />
              </IconButton>
            </Box>

            <Box className={classes.deletePostBody}>
              <Box className={classes.deletePostPreview}>
                {(isPaidPostType(postPendingDelete?.type) ||
                  Number(postPendingDelete?.price || 0) > 0) && (
                  <Box className={classes.deletePostPaidOverlay}>
                    <Typography className={classes.deletePostPaidLabel}>
                      Bundle price:
                    </Typography>
                    <Typography className={classes.deletePostPaidAmount}>
                      ${Number(postPendingDelete?.price || 0)}
                    </Typography>
                  </Box>
                )}
                {postPendingDelete?.content?.[0]?.type === "video" ? (
                  <video
                    className={`${classes.deletePostPreviewImg} ${
                      isPaidPostType(postPendingDelete?.type) ||
                      Number(postPendingDelete?.price || 0) > 0
                        ? classes.deletePostPreviewMediaPaid
                        : ""
                    }`}
                    src={postPendingDelete.content[0]?.mediaUrl}
                    muted
                    playsInline
                    autoPlay
                    loop
                  />
                ) : (
                  <img
                    className={`${classes.deletePostPreviewImg} ${
                      isPaidPostType(postPendingDelete?.type) ||
                      Number(postPendingDelete?.price || 0) > 0
                        ? classes.deletePostPreviewMediaPaid
                        : ""
                    }`}
                    src={postPendingDelete?.content?.[0]?.mediaUrl}
                    alt="Post preview"
                  />
                )}
              </Box>

              <Typography className={classes.deletePostQuestion}>
                Are you sure you want to delete this post?
              </Typography>
              {(isPaidPostType(postPendingDelete?.type) ||
                Number(postPendingDelete?.price || 0) > 0) && (
                <Typography className={classes.deletePostHint}>
                  Users who have already purchased it will continue to have
                  access to the included media, but it will no longer be visible
                  to other users.
                </Typography>
              )}
            </Box>

            <Box className={classes.deletePostActions}>
              <Button
                className={classes.deletePostCancelBtn}
                disabled={deletingPost}
                onClick={() => {
                  setDeletePostConfirmOpen(false);
                  setPostPendingDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                className={classes.deletePostDeleteBtn}
                disabled={deletingPost || !postPendingDelete?._id}
                onClick={async () => {
                  if (!postPendingDelete) return;
                  setDeletingPost(true);
                  try {
                    await handleDeletePost(postPendingDelete);
                    setDeletePostConfirmOpen(false);
                    setPostPendingDelete(null);
                    setEditPostDialogVisible(false);
                    setSelectedPostForEdit(null);
                    setRawHashtagInput("");
                    setEditSubscriptionType("basic");
                    restoreBodyScrollAfterEditPost();
                  } finally {
                    setDeletingPost(false);
                  }
                }}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Modal>
      </div>
    </>
  );
};

export default ProfilePage;
